import React, { useState, ChangeEvent, useMemo, useEffect, useRef } from 'react';
import { ArrowLeft, Plus, Trash2, Upload, Calendar, ArrowRight, Users, Download, Mail, Edit3, Save, X, ExternalLink, Loader2, ChevronUp, ChevronDown, Copy } from 'lucide-react';
import Papa from 'papaparse';
import { collection, doc, updateDoc, addDoc, serverTimestamp, onSnapshot, query, where, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { isSeminarDateExpired } from '../utils/seminarArchive';
import {
  SeminarScheduleDay,
  createEmptyScheduleDay,
  normalizeScheduleDays,
  sortScheduleDays,
  buildDateSummaryFromDays,
  formatGermanWeekday,
  formatScheduleDayLine,
  resolveSeminarDateText,
} from '../utils/seminarSchedule';
import {
  SeminarPromoCode,
  SeminarPromoCodeType,
  createEmptyPromoCode,
  normalizePromoCodes,
  normalizePromoCodeString,
} from '../utils/seminarPromoCodes';
import SeminarDinnerManager from '../components/seminar/SeminarDinnerManager';

// --- TYPES & INTERFACES ---
interface Participant {
  id: string;
  firma: string;
  vorname: string;
  nachname: string;
  strasse: string;
  plz: string;
  stadt: string;
  land: string;
  email: string;
  typ: 'bestand' | 'extern';
  zusatzPersonen: number;
  hatCatering: boolean;
  promoCode?: string;
  discountNetto?: number;
  discountLabel?: string;
  rechnungNr?: string;
  invoiceDate?: string;
  /** ISO-Zeitpunkt, sobald Rechnungs-Mail in mail_queue lag */
  invoiceMailSentAt?: string;
}

interface EventCost {
  id: string;
  description: string;
  amount: number;
}

interface EventDoc {
  id?: string;
  title: string;
  date: string;
  /** Strukturierte Seminartage mit Uhrzeiten (Landing + Admin) */
  scheduleDays?: SeminarScheduleDay[];
  invoiceDate?: string;
  location: string;
  locationAddress?: string;
  description: string;
  priceBestand: number;
  priceExtern: number;
  priceZusatz: number;
  priceCatering: number;
  promoCodes?: SeminarPromoCode[];
  participants: Participant[];
  costs?: EventCost[];
  lastInvoiceSequence: number;
  invoiceYear: number;
  createdAt?: any;
  marketingTeaser?: string;
  marketingBullets?: string[];
  marketingPitch?: string;
  marketingImage?: string;
  isOnline?: boolean;
  onlineLink?: string;
  /** In-App Popup-Promotion (Prio-2) */
  showPromotionPopup?: boolean;
  /** Nach Seminar-Ende — Rechnungen bleiben im Archiv erreichbar */
  isArchived?: boolean;
  archivedAt?: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const roundMoney = (amount: number) => Math.round((amount + Number.EPSILON) * 100) / 100;

const formatAmountDraft = (amount: number) => {
  if (!amount) return '';
  return amount.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const parseAmountDraft = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const normalized = trimmed.replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num) || num < 0) return 0;
  return roundMoney(num);
};

const isValidAmountDraft = (value: string) => /^[0-9]*([.,][0-9]*)?$/.test(value);

const getTodayIsoDate = () => new Date().toISOString().split('T')[0];

const formatInvoiceDate = (dateStr: string) => {
  const invoiceDateObj = new Date(dateStr);
  return !isNaN(invoiceDateObj.getTime()) ? invoiceDateObj.toLocaleDateString('de-DE') : dateStr;
};

const getParticipantInvoiceDate = (participant: Participant, event: EventDoc) =>
  participant.invoiceDate || event.invoiceDate || getTodayIsoDate();

const getMarketingTextareaRows = (text: string, minRows = 2) =>
  Math.max(minRows, text.split('\n').length, Math.ceil(text.length / 72) || 1);

const MARKETING_TEXTAREA_CLASSES = "bg-slate-900 border border-slate-700 text-white rounded-lg p-3 w-full font-sans text-sm focus:outline-none focus:border-[#E6C299] resize-y leading-relaxed whitespace-pre-wrap";

const CostAmountInput: React.FC<{
  amount: number;
  onCommit: (amount: number) => void;
}> = ({ amount, onCommit }) => {
  const [draft, setDraft] = useState(() => formatAmountDraft(amount));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDraft(formatAmountDraft(amount));
    }
  }, [amount, isFocused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onFocus={() => setIsFocused(true)}
      onChange={(e) => {
        const next = e.target.value;
        if (next === '' || isValidAmountDraft(next)) {
          setDraft(next);
        }
      }}
      onBlur={() => {
        setIsFocused(false);
        const parsed = parseAmountDraft(draft);
        onCommit(parsed);
        setDraft(formatAmountDraft(parsed));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="0,00"
      className="bg-slate-900 border border-slate-700 w-24 text-right text-white rounded p-1 text-xs font-mono"
    />
  );
};

/** Hauptperson + Zusatzpersonen (für Kopfzahl, nicht Anmeldungszeilen). */
const getParticipantHeadcount = (p: Participant) => 1 + (Number(p.zusatzPersonen) || 0);

const getParticipantTotals = (p: Participant, event: EventDoc) => {
  const isDE = p.land.toUpperCase() === 'DE';
  const vatRate = isDE ? 0.19 : 0;
  const baseNetto = p.typ === 'bestand' ? event.priceBestand : event.priceExtern;
  const zusatzNetto = p.zusatzPersonen * event.priceZusatz;
  const cateringNetto = p.hatCatering ? (1 + p.zusatzPersonen) * event.priceCatering : 0;
  const rawDiscount = Math.max(0, roundMoney(Number(p.discountNetto) || 0));
  const discountNetto = roundMoney(Math.min(baseNetto + zusatzNetto + cateringNetto, rawDiscount));
  const netto = roundMoney(Math.max(0, baseNetto + zusatzNetto + cateringNetto - discountNetto));
  const vat = roundMoney(netto * vatRate);
  const brutto = roundMoney(netto + vat);

  return {
    baseNetto,
    zusatzNetto,
    cateringNetto,
    discountNetto,
    netto,
    vat,
    brutto,
    isReverseCharge: !isDE,
  };
};

type Html2CanvasCaptureOptions = Html2Canvas.Html2CanvasOptions & {
  scale?: number;
  backgroundColor?: string;
};

const PDF_CAPTURE_OPTIONS: Html2CanvasCaptureOptions = {
  scale: 2,
  useCORS: true,
  logging: false,
  backgroundColor: '#ffffff',
};

// --- HILFSKOMPONENTE ---
const AdminInput: React.FC<any> = ({ label, textarea, ...props }) => {
  const baseClasses = "bg-slate-900 border border-slate-700 text-white rounded-lg p-3 w-full font-sans text-sm focus:outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299]/30 transition-all";
  return (
    <div className="mb-4">
      <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">{label}</label>
      {textarea ? (
        <textarea {...props} className={`${baseClasses} resize-none`} rows={3} />
      ) : (
        <input {...props} className={baseClasses} />
      )}
    </div>
  );
};

// --- HAUPTKOMPONENTE ---
const AdminInvoiceCreator: React.FC = () => {
  const [view, setView] = useState<'list' | 'create' | 'detail' | 'edit'>('list');
  const [events, setEvents] = useState<EventDoc[]>([]);
  const [activeEvent, setActiveEvent] = useState<EventDoc | null>(null);

  const [newEvent, setNewEvent] = useState<Partial<EventDoc>>({
    title: '', 
    date: '', 
    location: '', 
    locationAddress: '',
    description: '',
    priceBestand: 249, priceExtern: 349, priceZusatz: 149, priceCatering: 49,
    promoCodes: [],
    costs: [],
    marketingTeaser: '', marketingBullets: [''], marketingPitch: '', marketingImage: '',
    isOnline: false, onlineLink: '',
    showPromotionPopup: false,
    scheduleDays: [createEmptyScheduleDay()],
  });

  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [bulkMailProgress, setBulkMailProgress] = useState<{ done: number; total: number } | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [bufferedRegistrations, setBufferedRegistrations] = useState<any[]>([]);
  /** eventId → Anzahl status:neu (für Event-Karten-Badge) */
  const [pendingRegsByEventId, setPendingRegsByEventId] = useState<Record<string, number>>({});
  /** Bereits verarbeitet, aber noch ohne Bestätigungsmail */
  const [needsConfirmationRegs, setNeedsConfirmationRegs] = useState<any[]>([]);
  const [processingRegistrationId, setProcessingRegistrationId] = useState<string | null>(null);
  const [isResendingConfirmations, setIsResendingConfirmations] = useState(false);
  const [publicEventIds, setPublicEventIds] = useState<Set<string>>(new Set());
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [listTab, setListTab] = useState<'active' | 'archive'>('active');
  const autoArchiveInFlight = useRef<Set<string>>(new Set());
  const activeEventRef = useRef<EventDoc | null>(null);

  useEffect(() => {
    activeEventRef.current = activeEvent;
  }, [activeEvent]);

  const handleMarketingImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);
    try {
      const fileRef = ref(storage, `events/marketing/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setNewEvent({ ...newEvent, marketingImage: downloadURL });
    } catch (error) {
      console.error('Hero-Bild Upload fehlgeschlagen:', error);
      alert('Fehler beim Hochladen des Bildes.');
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  // --- FIREBASE: EVENTS LADEN + Auto-Archiv nach Seminar-Ende ---
  useEffect(() => {
    // 💉 CHIRURGIE-FIX: Query-Bypass (Frontend Sorting) gegen 400er Fehler
    const unsubscribe = onSnapshot(collection(db, 'events'), (snapshot) => {
      let loadedEvents = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as EventDoc));

      // Abgelaufene Seminare still archivieren (Teilnehmer/Rechnungen bleiben)
      loadedEvents.forEach((ev) => {
        if (!ev.id || ev.isArchived) return;
        if (!isSeminarDateExpired(resolveSeminarDateText(ev))) return;
        if (autoArchiveInFlight.current.has(ev.id)) return;
        autoArchiveInFlight.current.add(ev.id);
        const archivedAt = new Date().toISOString();
        updateDoc(doc(db, 'events', ev.id), { isArchived: true, archivedAt })
          .then(() => {
            // Landing deaktivieren — Archiv-Seminare sollen nicht öffentlich buchbar bleiben
            deleteDoc(doc(db, 'public_events', ev.id!)).catch(() => { /* ok wenn fehlte */ });
          })
          .catch((err) => console.error('Auto-Archiv events:', err))
          .finally(() => {
            autoArchiveInFlight.current.delete(ev.id!);
          });
        ev.isArchived = true;
        ev.archivedAt = archivedAt;
      });

      // Lokales Sortieren ersetzt das Firebase 'orderBy'
      loadedEvents.sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return timeB - timeA;
      });

      setEvents(loadedEvents);

      setActiveEvent(prevActive => {
        if (prevActive) {
            return loadedEvents.find(e => e.id === prevActive.id) || prevActive;
        }
        return prevActive;
      });
    }, (error) => {
        console.error("FATAL: Event Listener abgestürzt:", error);
    });
    return () => unsubscribe();
  }, []);

  // Öffentliche Landingpages (public_events) — fehlt der Eintrag → Landing "nicht gefunden"
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'public_events'), (snapshot) => {
      setPublicEventIds(new Set(snapshot.docs.map((d) => d.id)));
    }, (error) => {
      console.error('public_events Listener:', error);
    });
    return () => unsubscribe();
  }, []);

  // Globale Poststelle: neue Anmeldungen je Event (Event-Listen-Badges)
  useEffect(() => {
    const registrationsQuery = query(
      collection(db, 'seminar_anmeldungen'),
      where('status', '==', 'neu')
    );
    const unsubscribe = onSnapshot(registrationsQuery, (snapshot) => {
      const counts: Record<string, number> = {};
      snapshot.docs.forEach((docSnap) => {
        const eventId = String(docSnap.data()?.eventId || '');
        if (!eventId) return;
        counts[eventId] = (counts[eventId] || 0) + 1;
      });
      setPendingRegsByEventId(counts);
    }, (error) => {
      console.error('Globale Anmeldungs-Zähler:', error);
      setPendingRegsByEventId({});
    });
    return () => unsubscribe();
  }, []);

  // --- POSTSTELLE: GEPUFFERTE ANMELDUNGEN ---
  useEffect(() => {
    if (!activeEvent?.id) {
      setBufferedRegistrations([]);
      setNeedsConfirmationRegs([]);
      return;
    }

    const registrationsQuery = query(
      collection(db, 'seminar_anmeldungen'),
      where('eventId', '==', activeEvent.id)
    );

    const unsubscribe = onSnapshot(registrationsQuery, (snapshot) => {
      const registrations = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as any));

      const neu = registrations.filter((registration) => registration.status === 'neu');
      neu.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });
      setBufferedRegistrations(neu);

      // Nur Anmeldungen, die noch als Teilnehmer in DIESEM Event stehen
      // (alte Duplikate / gelöschte TN nicht mitzählen)
      const participantIds = new Set(
        (activeEvent.participants || []).map((p) => String(p.id || ''))
      );
      const participantEmails = new Set(
        (activeEvent.participants || [])
          .map((p) => String(p.email || '').trim().toLowerCase())
          .filter(Boolean)
      );

      const needsMail = registrations.filter((registration) => {
        if (registration.status !== 'verarbeitet') return false;
        if (registration.confirmationMailSentAt) return false;
        const linkedId = `reg-${registration.id}`;
        if (participantIds.has(linkedId)) return true;
        const email = String(registration.formData?.email || '').trim().toLowerCase();
        return !!email && participantEmails.has(email);
      });
      needsMail.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });
      setNeedsConfirmationRegs(needsMail);
    }, (error) => {
      console.error('Poststelle: Fehler beim Laden der Anmeldungen', error);
      setBufferedRegistrations([]);
      setNeedsConfirmationRegs([]);
    });

    return () => unsubscribe();
  }, [activeEvent?.id, activeEvent?.participants]);

  /** Bestätigungsmail: Anmeldung ok, Rechnung erst kurz vor dem Seminar */
  const enqueueSeminarConfirmationMail = async (registration: any, event: EventDoc) => {
    const formData = registration.formData || {};
    const recipientEmail = String(formData.email || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipientEmail || !emailRegex.test(recipientEmail)) {
      return false;
    }
    if (registration.confirmationMailSentAt) {
      return false;
    }

    const firstName = String(formData.vorname || formData.nachname || 'Teilnehmer').trim();
    const eventTitle = String(event.title || 'Seminar').trim();
    const scheduleLines = sortScheduleDays(normalizeScheduleDays(event.scheduleDays)).map((d) =>
      formatScheduleDayLine(d)
    );
    const eventDateLabel =
      scheduleLines.length > 0
        ? scheduleLines.join('; ')
        : String(event.date || '').trim();
    const subject = `Anmeldung bestätigt: ${eventTitle}`;
    const messageHtml = [
      `<p>Liebe/r ${firstName},</p>`,
      `<p>vielen Dank — deine Anmeldung für <strong>${eventTitle}</strong>${eventDateLabel ? ` (${eventDateLabel})` : ''} ist bei uns eingegangen und fest vorgemerkt.</p>`,
      `<p><strong>Wichtig:</strong> Die Rechnung erhältst du erst kurz vor dem Seminar. Du musst jetzt nichts weiter tun.</p>`,
      `<p>Bei Fragen antworte einfach auf diese E-Mail.</p>`,
      `<p>Herzliche Grüße<br/>Dein PreisExpert-Team</p>`,
    ].join('');

    await addDoc(collection(db, 'mail_queue'), {
      type: 'change_request',
      email: recipientEmail,
      subject,
      messageHtml,
      mailTitle: 'Seminar-Anmeldung',
      participantName: `${formData.vorname || ''} ${formData.nachname || ''}`.trim(),
      eventName: eventTitle,
      appBaseUrl: window.location.origin,
      status: 'pending',
      createdAt: serverTimestamp(),
    });

    await updateDoc(doc(db, 'seminar_anmeldungen', registration.id), {
      confirmationMailSentAt: serverTimestamp(),
    });
    return true;
  };

  const handleResendConfirmationMails = async () => {
    if (!activeEvent?.id || isResendingConfirmations || needsConfirmationRegs.length === 0) return;
    const count = needsConfirmationRegs.length;
    if (
      !window.confirm(
        `${count} bereits verarbeitete Anmeldung(en) ohne Bestätigungsmail gefunden.\n\nJetzt die Info-Mail nachsenden (Anmeldung ok, Rechnung erst kurz vor dem Seminar)?`
      )
    ) {
      return;
    }

    setIsResendingConfirmations(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const registration of needsConfirmationRegs) {
        try {
          const sent = await enqueueSeminarConfirmationMail(registration, activeEvent);
          if (sent) ok += 1;
        } catch (err) {
          console.error('Bestätigungsmail Nachversand fehlgeschlagen:', registration.id, err);
          fail += 1;
        }
      }
      alert(`Bestätigungsmails: ${ok} in die Queue gelegt${fail ? `, ${fail} fehlgeschlagen` : ''}.`);
    } finally {
      setIsResendingConfirmations(false);
    }
  };

  // --- LOGIK: EVENT ERSTELLEN ---
  const handleCreateEvent = async () => {
    setIsSaving(true);
    try {
      const invoiceYear = new Date().getFullYear();
      const scheduleDays = normalizeScheduleDays(newEvent.scheduleDays || []);
      const dateLabel =
        buildDateSummaryFromDays(scheduleDays) || newEvent.date || 'TBA';
      
      const cleanPromoCodes = normalizePromoCodes(newEvent.promoCodes);

      const eventPayload: EventDoc = {
        title: newEvent.title || 'Neues Event',
        date: dateLabel,
        scheduleDays,
        location: newEvent.location || '',
        locationAddress: newEvent.locationAddress || '',
        description: newEvent.description || '',
        priceBestand: newEvent.priceBestand!,
        priceExtern: newEvent.priceExtern!,
        priceZusatz: newEvent.priceZusatz!,
        priceCatering: newEvent.priceCatering!,
        promoCodes: cleanPromoCodes,
        participants: [],
        costs: [],
        lastInvoiceSequence: 0,
        invoiceYear,
        marketingTeaser: newEvent.marketingTeaser || '',
        marketingBullets: (newEvent.marketingBullets || []).filter(b => b.trim() !== ''),
        marketingPitch: newEvent.marketingPitch || '',
        marketingImage: newEvent.marketingImage || '',
        isOnline: newEvent.isOnline || false,
        onlineLink: newEvent.isOnline ? (newEvent.onlineLink || '') : '',
        showPromotionPopup: newEvent.showPromotionPopup === true,
      };
      
      const docRef = await addDoc(collection(db, 'events'), {
        ...eventPayload,
        isArchived: false,
        createdAt: serverTimestamp()
      });

      // Spiegel-Dokument: nur Marketing-/Preisfelder (DSGVO-freundliche Öffentlichkeit)
      const publicPayload = {
        title: eventPayload.title,
        date: eventPayload.date,
        scheduleDays: eventPayload.scheduleDays || [],
        location: eventPayload.location,
        locationAddress: eventPayload.locationAddress || '',
        marketingTeaser: eventPayload.marketingTeaser || '',
        marketingBullets: eventPayload.marketingBullets || [],
        marketingPitch: eventPayload.marketingPitch || '',
        marketingImage: eventPayload.marketingImage || '',
        priceBestand: eventPayload.priceBestand,
        priceExtern: eventPayload.priceExtern,
        priceZusatz: eventPayload.priceZusatz,
        promoCodes: cleanPromoCodes,
        isOnline: eventPayload.isOnline || false,
        showPromotionPopup: eventPayload.showPromotionPopup === true,
      };

      await setDoc(doc(db, 'public_events', docRef.id), publicPayload);
      
      setActiveEvent({ id: docRef.id, ...eventPayload });
      setView('detail');
    } catch (error) {
      console.error(error);
      alert("Fehler beim Erstellen des Events");
    } finally {
      setIsSaving(false);
    }
  };

  // --- LOGIK: EVENT BEARBEITEN (UPDATE) ---
  const handleUpdateEvent = async () => {
    if (!activeEvent?.id) return;
    setIsSaving(true);
    try {
      const scheduleDays = normalizeScheduleDays(newEvent.scheduleDays || []);
      const dateLabel =
        buildDateSummaryFromDays(scheduleDays) || newEvent.date || '';
      const cleanPromoCodes = normalizePromoCodes(newEvent.promoCodes);

      await updateDoc(doc(db, 'events', activeEvent.id), {
        title: newEvent.title || '',
        date: dateLabel,
        scheduleDays,
        location: newEvent.location || '',
        locationAddress: newEvent.locationAddress || '',
        description: newEvent.description || '',
        priceBestand: newEvent.priceBestand,
        priceExtern: newEvent.priceExtern,
        priceZusatz: newEvent.priceZusatz,
        priceCatering: newEvent.priceCatering,
        promoCodes: cleanPromoCodes,
        marketingTeaser: newEvent.marketingTeaser || '',
        marketingBullets: (newEvent.marketingBullets || []).filter(b => b.trim() !== ''),
        marketingPitch: newEvent.marketingPitch || '',
        marketingImage: newEvent.marketingImage || '',
        isOnline: newEvent.isOnline || false,
        onlineLink: newEvent.isOnline ? (newEvent.onlineLink || '') : '',
        showPromotionPopup: newEvent.showPromotionPopup === true,
      });

      // Spiegel-Dokument: nur Marketing-/Preisfelder (DSGVO-freundliche Öffentlichkeit)
      const publicPayload = {
        title: newEvent.title || '',
        date: dateLabel,
        scheduleDays,
        location: newEvent.location || '',
        locationAddress: newEvent.locationAddress || '',
        marketingTeaser: newEvent.marketingTeaser || '',
        marketingBullets: (newEvent.marketingBullets || []).filter(b => b.trim() !== ''),
        marketingPitch: newEvent.marketingPitch || '',
        marketingImage: newEvent.marketingImage || '',
        priceBestand: newEvent.priceBestand!,
        priceExtern: newEvent.priceExtern!,
        priceZusatz: newEvent.priceZusatz!,
        promoCodes: cleanPromoCodes,
        isOnline: newEvent.isOnline || false,
        showPromotionPopup: newEvent.showPromotionPopup === true,
      };

      await setDoc(doc(db, 'public_events', activeEvent.id), publicPayload, { merge: true });
      setActiveEvent({
        ...activeEvent,
        title: newEvent.title || '',
        date: dateLabel,
        scheduleDays,
        location: newEvent.location || '',
        locationAddress: newEvent.locationAddress || '',
        description: newEvent.description || '',
        priceBestand: newEvent.priceBestand!,
        priceExtern: newEvent.priceExtern!,
        priceZusatz: newEvent.priceZusatz!,
        priceCatering: newEvent.priceCatering!,
        promoCodes: cleanPromoCodes,
        marketingTeaser: newEvent.marketingTeaser || '',
        marketingBullets: (newEvent.marketingBullets || []).filter(b => b.trim() !== ''),
        marketingPitch: newEvent.marketingPitch || '',
        marketingImage: newEvent.marketingImage || '',
        isOnline: newEvent.isOnline || false,
        onlineLink: newEvent.isOnline ? (newEvent.onlineLink || '') : '',
        showPromotionPopup: newEvent.showPromotionPopup === true,
      });
      setView('detail');
    } catch (error) {
      console.error(error);
      alert("Fehler beim Aktualisieren des Events");
    } finally {
      setIsSaving(false);
    }
  };

  // --- LOGIK: EVENT LÖSCHEN (inkl. kaputter Landingpage-Spiegel) ---
  const handleDeleteEvent = async (eventToDelete: EventDoc) => {
    if (!eventToDelete?.id || isDeletingEvent) return;
    const participantCount = eventToDelete.participants?.length || 0;
    const landingOk = publicEventIds.has(eventToDelete.id);
    const confirmMsg =
      `Event wirklich unwiderruflich löschen?\n\n` +
      `„${eventToDelete.title || 'Ohne Titel'}“ (${eventToDelete.date || 'ohne Datum'})\n` +
      `Teilnehmer im Event: ${participantCount}\n` +
      `Landingpage: ${landingOk ? 'vorhanden' : 'FEHLT / kaputt'}\n\n` +
      `Es wird aus der Event-Liste UND aus public_events entfernt.`;
    if (!window.confirm(confirmMsg)) return;

    setIsDeletingEvent(true);
    try {
      await deleteDoc(doc(db, 'events', eventToDelete.id));
      try {
        await deleteDoc(doc(db, 'public_events', eventToDelete.id));
      } catch (pubErr) {
        console.warn('public_events konnte nicht gelöscht werden (evtl. fehlte schon):', pubErr);
      }
      if (activeEvent?.id === eventToDelete.id) {
        setActiveEvent(null);
        setView('list');
      }
    } catch (error) {
      console.error('Event löschen fehlgeschlagen:', error);
      alert('Event konnte nicht gelöscht werden.');
    } finally {
      setIsDeletingEvent(false);
    }
  };

  const handleRestoreFromArchive = async (eventToRestore: EventDoc) => {
    if (!eventToRestore?.id) return;
    if (!window.confirm(`„${eventToRestore.title}“ aus dem Archiv zurückholen?`)) return;
    try {
      await updateDoc(doc(db, 'events', eventToRestore.id), {
        isArchived: false,
        archivedAt: '',
      });
      // Landing ggf. wieder spiegeln beim nächsten Speichern — hier nur Flag
      setListTab('active');
    } catch (error) {
      console.error('Restore fehlgeschlagen:', error);
      alert('Wiederherstellen fehlgeschlagen.');
    }
  };

  /** Archiv als Vorlage: neues Event (ohne Teilnehmer/Rechnungen), Original bleibt im Archiv */
  const handleUseArchiveAsTemplate = (source: EventDoc) => {
    setNewEvent({
      title: source.title || '',
      date: '',
      location: source.location || '',
      locationAddress: source.locationAddress || '',
      description: source.description || '',
      priceBestand: source.priceBestand,
      priceExtern: source.priceExtern,
      priceZusatz: source.priceZusatz,
      priceCatering: source.priceCatering,
      marketingTeaser: source.marketingTeaser || '',
      marketingBullets: source.marketingBullets?.length ? [...source.marketingBullets] : [''],
      marketingPitch: source.marketingPitch || '',
      marketingImage: source.marketingImage || '',
      isOnline: source.isOnline || false,
      onlineLink: source.onlineLink || '',
      showPromotionPopup: false,
      isArchived: false,
      scheduleDays: [createEmptyScheduleDay()],
    });
    setActiveEvent(null);
    setListTab('active');
    setView('create');
  };

  const activeEvents = useMemo(() => events.filter((e) => !e.isArchived), [events]);
  const archivedEventsList = useMemo(() => events.filter((e) => !!e.isArchived), [events]);
  const visibleEvents = listTab === 'archive' ? archivedEventsList : activeEvents;

  // --- LOGIK: CSV UPLOAD & AUTO-NUMMERIERUNG ---
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeEvent) return;
    setCsvError(null);

    Papa.parse(file, {
      delimiter: ";",
      skipEmptyLines: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          setCsvError("Fehler beim Parsen. Ist es eine Semikolon-getrennte CSV?");
          return;
        }
        
        const data = results.data as string[][];
        const newParticipants: Participant[] = [];
        let currentSeq = activeEvent.lastInvoiceSequence || 0;

        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          if (row.length < 5) continue;
          if (i === 0 && row[0].toLowerCase().includes('vorname')) continue;

          currentSeq++;
          const formattedSeq = currentSeq.toString().padStart(2, '0');
          const newNr = `S${formattedSeq}/${activeEvent.invoiceYear}`;

          const vorname = row[0] || '';
          const nachname = row[2] || '';
          const firma = row[3] || '';
          const strasse = row[4] || '';
          const email = row[5] || '';
          const anzahlRaw = parseInt(row[8]) || 1;
          const zusatzPersonen = anzahlRaw > 1 ? anzahlRaw - 1 : 0;

          newParticipants.push({
            id: `csv-${Date.now()}-${i}`,
            firma: firma,
            vorname: vorname,
            nachname: nachname,
            strasse: strasse,
            plz: '', 
            stadt: '', 
            land: 'DE',
            email: email,
            typ: 'bestand', 
            zusatzPersonen: zusatzPersonen,
            hatCatering: true,
            rechnungNr: newNr,
            invoiceDate: getTodayIsoDate(),
          });
        }

        const updatedParticipants = [...activeEvent.participants, ...newParticipants];
        await updateDoc(doc(db, 'events', activeEvent.id!), {
          participants: updatedParticipants,
          lastInvoiceSequence: currentSeq
        });
      }
    });
  };

  const addManualRow = async () => {
    if (!activeEvent) return;
    
    let currentSeq = activeEvent.lastInvoiceSequence || 0;
    currentSeq++;
    const formattedSeq = currentSeq.toString().padStart(2, '0');
    const newNr = `S${formattedSeq}/${activeEvent.invoiceYear}`;

    const newP: Participant = {
      id: `manual-${Date.now()}`,
      firma: '', vorname: '', nachname: 'NEU', strasse: '', plz: '', stadt: '', land: 'DE', email: '', typ: 'extern', zusatzPersonen: 0, hatCatering: false,
      rechnungNr: newNr,
      invoiceDate: getTodayIsoDate(),
    };
    
    const updated = [...activeEvent.participants, newP];
    await updateDoc(doc(db, 'events', activeEvent.id!), { 
        participants: updated,
        lastInvoiceSequence: currentSeq 
    });
    setPreviewIndex(updated.length - 1);
  };

  const handleProcessRegistration = async (registration: any) => {
    if (!activeEvent?.id || processingRegistrationId) return;

    const formData = registration.formData || {};
    setProcessingRegistrationId(registration.id);

    try {
      let currentSeq = activeEvent.lastInvoiceSequence || 0;
      currentSeq++;
      const formattedSeq = currentSeq.toString().padStart(2, '0');
      const newNr = `S${formattedSeq}/${activeEvent.invoiceYear}`;

      const financials = registration.financials || {};
      const promoCode = String(financials.promoCode || formData.promoCode || '').trim().toUpperCase();
      const discountNetto = roundMoney(Math.max(0, Number(financials.discountNetto) || 0));
      const discountLabel = String(financials.discountLabel || '').trim();

      const newParticipant: Participant = {
        id: `reg-${registration.id}`,
        firma: formData.firma || '',
        vorname: formData.vorname || '',
        nachname: formData.nachname || '',
        strasse: formData.strasse || '',
        plz: formData.plz || '',
        stadt: formData.ort || '',
        land: 'DE',
        email: formData.email || '',
        typ: formData.isMandant ? 'bestand' : 'extern',
        zusatzPersonen: Number(formData.zusatzPersonen) || 0,
        hatCatering: false,
        ...(promoCode ? { promoCode } : {}),
        ...(discountNetto > 0 ? { discountNetto } : {}),
        ...(discountLabel ? { discountLabel } : {}),
        rechnungNr: newNr,
        invoiceDate: getTodayIsoDate(),
      };

      const updatedParticipants = [...activeEvent.participants, newParticipant];

      await updateDoc(doc(db, 'events', activeEvent.id), {
        participants: updatedParticipants,
        lastInvoiceSequence: currentSeq,
      });

      let mailQueued = false;
      try {
        mailQueued = await enqueueSeminarConfirmationMail(registration, activeEvent);
      } catch (mailErr) {
        console.warn('Bestätigungsmail nach Verarbeitung fehlgeschlagen:', mailErr);
      }

      await updateDoc(doc(db, 'seminar_anmeldungen', registration.id), {
        status: 'verarbeitet',
      });

      setPreviewIndex(updatedParticipants.length - 1);
      if (!mailQueued && String(formData.email || '').trim()) {
        // E-Mail vorhanden, aber Queue fehlgeschlagen / ungültig
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formData.email || '').trim());
        if (emailOk && !registration.confirmationMailSentAt) {
          alert('Teilnehmer übernommen — Bestätigungsmail konnte nicht in die Queue. Bitte über „Bestätigung nachsenden“ erneut versuchen.');
        }
      }
    } catch (error) {
      console.error('Fehler beim Verarbeiten der Anmeldung:', error);
      alert('Die Anmeldung konnte nicht verarbeitet werden.');
    } finally {
      setProcessingRegistrationId(null);
    }
  };

  const updateParticipant = async (id: string, field: keyof Participant, value: any) => {
    if (!activeEvent) return;
    const updated = activeEvent.participants.map(p => p.id === id ? { ...p, [field]: value } : p);
    setActiveEvent({ ...activeEvent, participants: updated });
    await updateDoc(doc(db, 'events', activeEvent.id!), { participants: updated });
  };

  const removeParticipant = async (id: string) => {
    if (!activeEvent) return;
    const updated = activeEvent.participants.filter(p => p.id !== id);
    await updateDoc(doc(db, 'events', activeEvent.id!), { participants: updated });
    if(previewIndex >= updated.length) setPreviewIndex(Math.max(0, updated.length - 1));
  };

  const saveCosts = async (updatedCosts: EventCost[]) => {
    if (!activeEvent?.id) return;
    setActiveEvent({ ...activeEvent, costs: updatedCosts });
    await updateDoc(doc(db, 'events', activeEvent.id), { costs: updatedCosts });
  };

  const addCostRow = async () => {
    if (!activeEvent) return;
    const updatedCosts = [
      ...(activeEvent.costs || []),
      { id: `cost-${Date.now()}`, description: '', amount: 0 }
    ];
    await saveCosts(updatedCosts);
  };

  const updateCost = async (id: string, field: keyof EventCost, value: string | number) => {
    if (!activeEvent) return;
    const updatedCosts = (activeEvent.costs || []).map(c =>
      c.id === id ? { ...c, [field]: value } : c
    );
    await saveCosts(updatedCosts);
  };

  const removeCost = async (id: string) => {
    if (!activeEvent) return;
    const updatedCosts = (activeEvent.costs || []).filter(c => c.id !== id);
    await saveCosts(updatedCosts);
  };

  // --- LOGIK: SAMMEL-PDF ERSTELLEN UND DOWNLOADEN ---
  const handleGenerateInvoices = async () => {
    if (!activeEvent || activeEvent.participants.length === 0) return;
    
    const hasMissingData = activeEvent.participants.some(p => !p.plz || !p.stadt || !p.strasse);
    if (hasMissingData) {
        if(!window.confirm("Einige Teilnehmer haben keine vollständige Adresse. Trotzdem PDFs generieren?")) return;
    }

    try {
      setIsGeneratingPDF(true); 

      setTimeout(async () => {
          try {
              const pdf = new jsPDF('p', 'mm', 'a4');
              // 💉 CHIRURGIE-FIX: ID auf event-print-factory geändert
              const printArea = document.getElementById('event-print-factory');
              if (!printArea) throw new Error("Interner Bereich nicht gefunden");

              const invoiceElements = printArea.querySelectorAll('.invoice-print-container');
              if (invoiceElements.length === 0) throw new Error("Keine Teilnehmer zu drucken");

              for (let i = 0; i < invoiceElements.length; i++) {
                  const element = invoiceElements[i] as HTMLElement;
                  const canvas = await html2canvas(element, PDF_CAPTURE_OPTIONS);
                  const imgData = canvas.toDataURL('image/jpeg', 1.0);
                  
                  const pdfWidth = pdf.internal.pageSize.getWidth();
                  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

                  if (i > 0) pdf.addPage();
                  pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
              }

              const safeTitle = activeEvent.title.replace(/[^a-zA-Z0-9]/g, '_');
              pdf.save(`Sammelrechnungen_${safeTitle}.pdf`);

          } catch (pdfError) {
              console.error(pdfError);
              alert("Fehler beim Zeichnen der PDF-Datei.");
          } finally {
              setIsGeneratingPDF(false);
          }
      }, 500); 

    } catch (e) {
      console.error(e);
      alert("Fehler bei der Sicherung.");
      setIsGeneratingPDF(false);
    }
  };

  // --- LOGIK: EINZEL-PDF DOWNLOAD ---
  const downloadSinglePDF = async (index: number, participant: Participant) => {
    setIsGeneratingPDF(true);
    setPreviewIndex(index); 

    setTimeout(async () => {
        try {
            // 💉 CHIRURGIE-FIX: ID auf event-print-factory geändert
            const printArea = document.getElementById('event-print-factory');
            if (!printArea) throw new Error("Print Factory nicht gefunden");
            
            const elements = printArea.querySelectorAll('.invoice-print-container');
            const element = elements[index] as HTMLElement;
            if (!element) throw new Error("Teilnehmer-Dokument nicht gefunden");

            const canvas = await html2canvas(element, PDF_CAPTURE_OPTIONS);
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            
            const fileName = participant.rechnungNr 
                ? `Rechnung_${participant.rechnungNr.replace('/', '-')}_${participant.nachname}.pdf` 
                : `Entwurf_${participant.nachname}.pdf`;
                
            pdf.save(fileName);
        } catch (error) {
            console.error("PDF Generierung fehlgeschlagen:", error);
            alert("Fehler beim Erstellen der Einzel-PDF.");
        } finally {
            setIsGeneratingPDF(false);
        }
    }, 500);
  };

  // --- LOGIK: MAIL VERSAND ÜBER DIE DATENBANK ---
  const captureParticipantPdfBase64 = async (participantIndex: number): Promise<string> => {
    setPreviewIndex(participantIndex);
    await new Promise((r) => setTimeout(r, 600));

    const printArea = document.getElementById('event-print-factory');
    const elements = printArea?.querySelectorAll('.invoice-print-container');
    const element = elements?.[participantIndex] as HTMLElement;
    if (!element) throw new Error('Element in der Print Factory nicht gefunden');

    const canvas = await html2canvas(element, PDF_CAPTURE_OPTIONS);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    return pdf.output('datauristring');
  };

  const enqueueParticipantInvoiceMail = async (
    participant: Participant,
    base64PDF: string
  ): Promise<void> => {
    const event = activeEventRef.current;
    if (!event?.id) throw new Error('Kein aktives Event');

    await addDoc(collection(db, 'mail_queue'), {
      email: participant.email.trim(),
      participantName: participant.vorname || participant.nachname,
      eventName: event.title,
      eventLocation: event.locationAddress || event.location || '',
      isOnline: event.isOnline || false,
      onlineLink: event.isOnline ? (event.onlineLink || '') : '',
      invoiceNr: participant.rechnungNr,
      pdfBase64: base64PDF,
      appBaseUrl: window.location.origin,
      status: 'pending',
      createdAt: serverTimestamp(),
    });

    const sentAt = new Date().toISOString();
    const updatedParticipants = event.participants.map((p) =>
      p.id === participant.id ? { ...p, invoiceMailSentAt: sentAt } : p
    );
    const nextEvent = { ...event, participants: updatedParticipants };
    await updateDoc(doc(db, 'events', event.id), {
      participants: updatedParticipants,
    });
    activeEventRef.current = nextEvent;
    setActiveEvent(nextEvent);
  };

  const handleSendMail = async (participant: Participant, options?: { silent?: boolean }) => {
    const silent = !!options?.silent;
    if (!participant.email) {
      if (!silent) alert('Fehler: Bitte trage erst eine E-Mail Adresse bei diesem Teilnehmer ein!');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(participant.email)) {
      if (!silent) {
        alert(
          `ACHTUNG: Die E-Mail-Adresse "${participant.email}" ist ungültig!\n\nBitte prüfe auf fehlende Zeichen (z.B. @ oder eine Endung wie .de/.com) oder versteckte Leerzeichen.`
        );
      }
      return false;
    }

    if (!participant.rechnungNr) {
      if (!silent) alert('Bitte weise erst eine Rechnungsnummer zu.');
      return false;
    }

    const event = activeEventRef.current;
    if (!event) return false;

    try {
      if (!silent) setIsGeneratingPDF(true);
      const participantIndex = event.participants.findIndex((p) => p.id === participant.id);
      if (participantIndex < 0) throw new Error('Teilnehmer nicht gefunden');

      const base64PDF = await captureParticipantPdfBase64(participantIndex);
      await enqueueParticipantInvoiceMail(participant, base64PDF);

      if (!silent) {
        alert('✅ E-Mail-Auftrag wurde erfolgreich an den Server übergeben! Er wird in wenigen Sekunden gesendet.');
      }
      return true;
    } catch (error) {
      console.error('Fehler beim Eintragen in die Datenbank:', error);
      if (!silent) alert('Fehler: Konnte E-Mail-Auftrag nicht erstellen.');
      return false;
    } finally {
      if (!silent) setIsGeneratingPDF(false);
    }
  };

  /** Sammelversand: AUSSCHLIESSLICH noch nicht versendete Rechnungen */
  const handleBulkSendMails = async () => {
    if (!activeEvent || isGeneratingPDF || bulkMailProgress) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Immer am frischen Ref filtern — nie bereits versendete erneut anfassen
    const freshParticipants = activeEventRef.current?.participants || activeEvent.participants;
    const targets = freshParticipants.filter(
      (p) =>
        !p.invoiceMailSentAt &&
        !!p.rechnungNr &&
        !!p.email &&
        emailRegex.test(String(p.email).trim())
    );

    if (targets.length === 0) {
      alert(
        'Keine offenen Rechnungs-Mails.\n\nBereits versendete Mails werden beim Sammelversand bewusst nicht erneut gesendet.\nEinzelnachversand bleibt über das Brief-Symbol möglich.'
      );
      return;
    }

    if (
      !window.confirm(
        `${targets.length} offene Rechnung(en) jetzt per E-Mail absenden?\n\nNur unerledigte Mails — bereits versendete Teilnehmer werden übersprungen.`
      )
    ) {
      return;
    }

    setIsGeneratingPDF(true);
    setBulkMailProgress({ done: 0, total: targets.length });
    let ok = 0;
    const errors: string[] = [];

    try {
      for (const participant of targets) {
        // Sicherheitsnetz: zwischenzeitlich doch schon versendet? → skip
        const latest = (activeEventRef.current?.participants || []).find((p) => p.id === participant.id);
        if (latest?.invoiceMailSentAt) {
          setBulkMailProgress((prev) =>
            prev ? { ...prev, done: Math.min(prev.done + 1, prev.total) } : prev
          );
          continue;
        }

        const label = `${participant.vorname || ''} ${participant.nachname || ''}`.trim() || participant.email;
        try {
          const sent = await handleSendMail(participant, { silent: true });
          if (sent) ok += 1;
          else errors.push(`${label}: übersprungen / fehlgeschlagen`);
        } catch (err) {
          console.error('Sammel-Mail fehlgeschlagen:', label, err);
          errors.push(`${label}: Versand fehlgeschlagen`);
        }
        setBulkMailProgress((prev) =>
          prev ? { ...prev, done: Math.min(prev.done + 1, prev.total) } : prev
        );
      }

      const summary =
        `Sammelversand (nur offene): ${ok} von ${targets.length} erfolgreich übergeben.` +
        (errors.length ? `\n\nHinweise:\n• ${errors.join('\n• ')}` : '');
      alert(summary);
    } finally {
      setIsGeneratingPDF(false);
      setBulkMailProgress(null);
    }
  };

  // --- VORSCHAU BERECHNUNG ---
  const previewInvoiceData = useMemo(() => {
    if (!activeEvent || activeEvent.participants.length === 0) return null;
    const p = activeEvent.participants[previewIndex];
    if (!p) return null;
    
    const totals = getParticipantTotals(p, activeEvent);
    const netto = totals.netto;
    const vat = totals.vat;
    const brutto = totals.brutto;

    const nr = p.rechnungNr || `SXX/${activeEvent.invoiceYear}`;
    const formattedDate = formatInvoiceDate(getParticipantInvoiceDate(p, activeEvent));

    return { 
        participant: p, baseNetto: totals.baseNetto, zusatzNetto: totals.zusatzNetto, cateringNetto: totals.cateringNetto, discountNetto: totals.discountNetto, netto, vat, brutto, nr, 
        isReverseCharge: totals.isReverseCharge, formattedDate: formattedDate 
    };
  }, [activeEvent, previewIndex]);

  // 💉 CHIRURGIE-FIX: GLOBALE FINANZ-BERECHNUNG FÜR DIE SUMMARY BAR
  const financialTotals = useMemo(() => {
    if (!activeEvent) return { registrations: 0, participants: 0, netto: 0, brutto: 0, totalCosts: 0, netProfit: 0, grossProfit: 0 };
    
    let sumNetto = 0;
    let sumBrutto = 0;
    let participants = 0;
    
    activeEvent.participants.forEach(p => {
        const totals = getParticipantTotals(p, activeEvent);
        sumNetto = roundMoney(sumNetto + totals.netto);
        sumBrutto = roundMoney(sumBrutto + totals.brutto);
        participants += getParticipantHeadcount(p);
    });

    const totalCosts = roundMoney((activeEvent.costs || []).reduce((sum, c) => sum + (Number(c.amount) || 0), 0));
    const netProfit = roundMoney(sumNetto - totalCosts);
    const grossProfit = roundMoney(sumBrutto - totalCosts);
    
    return {
      registrations: activeEvent.participants.length,
      participants,
      netto: sumNetto,
      brutto: sumBrutto,
      totalCosts,
      netProfit,
      grossProfit,
    };
  }, [activeEvent]);

  // --- UI RENDER: TEILNEHMER-TABELLE ---
  const renderTableRows = () => {
    if (!activeEvent) return null;
    return activeEvent.participants.map((p, index) => {
      const isSelected = index === previewIndex;
      return (
        <tr 
            key={p.id} 
            onClick={() => setPreviewIndex(index)}
            className={`cursor-pointer group transition-colors ${isSelected ? 'bg-slate-800' : 'hover:bg-slate-800/50'} ${p.rechnungNr ? 'border-l-4 border-blue-500' : (p.typ === 'bestand' ? 'border-l-4 border-emerald-500' : 'border-l-4 border-amber-500')}`}
        >
          <td className="p-2 w-1/3">
             <input type="text" value={p.firma} onChange={(e) => updateParticipant(p.id, 'firma', e.target.value)} placeholder="Salon" className="bg-transparent border-b border-slate-700 w-full text-white focus:outline-none focus:border-[#E6C299] text-xs p-1" />
             <div className="flex gap-1 mt-1">
                 <input type="text" value={p.vorname} onChange={(e) => updateParticipant(p.id, 'vorname', e.target.value)} placeholder="Vorname" className="bg-transparent border-b border-slate-700 w-1/2 text-white font-bold focus:outline-none focus:border-[#E6C299] text-xs p-1" />
                 <input type="text" value={p.nachname} onChange={(e) => updateParticipant(p.id, 'nachname', e.target.value)} placeholder="Nachname" className="bg-transparent border-b border-slate-700 w-1/2 text-white font-bold focus:outline-none focus:border-[#E6C299] text-xs p-1" />
             </div>
             <input type="email" value={p.email} onChange={(e) => updateParticipant(p.id, 'email', e.target.value)} placeholder="E-Mail Adresse" className={`bg-transparent border-b ${p.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email) ? 'border-red-500 text-red-400' : 'border-slate-700 text-blue-400'} w-full focus:outline-none focus:border-[#E6C299] text-xs p-1 mt-1`} />
          </td>
          <td className="p-2 w-1/3">
             <input type="text" value={p.strasse} onChange={(e) => updateParticipant(p.id, 'strasse', e.target.value)} placeholder="Straße + Nr." className="bg-transparent border-b border-slate-700 w-full text-slate-300 focus:outline-none focus:border-[#E6C299] text-xs p-1" />
             <div className="flex gap-1 mt-1">
                <input type="text" value={p.plz} onChange={(e) => updateParticipant(p.id, 'plz', e.target.value)} placeholder="PLZ" className={`bg-transparent border-b ${!p.plz ? 'border-red-500/50 bg-red-900/10' : 'border-slate-700'} w-1/3 text-slate-300 focus:outline-none focus:border-[#E6C299] text-xs p-1`} />
                <input type="text" value={p.stadt} onChange={(e) => updateParticipant(p.id, 'stadt', e.target.value)} placeholder="Stadt" className={`bg-transparent border-b ${!p.stadt ? 'border-red-500/50 bg-red-900/10' : 'border-slate-700'} w-2/3 text-slate-300 focus:outline-none focus:border-[#E6C299] text-xs p-1`} />
             </div>
          </td>
          <td className="p-2 text-center">
             <select value={p.typ} onChange={(e) => updateParticipant(p.id, 'typ', e.target.value)} className="bg-slate-900 border border-slate-700 text-[#E6C299] text-[10px] p-1 rounded w-full mb-1 outline-none">
                 <option value="bestand">Bestand</option>
                 <option value="extern">Extern</option>
             </select>
             <div className="flex items-center justify-between gap-1 text-[10px] text-slate-400 mt-2">
                 <span>Zusatz P.</span>
                 <input type="number" min="0" value={p.zusatzPersonen} onChange={(e) => updateParticipant(p.id, 'zusatzPersonen', parseInt(e.target.value) || 0)} className="bg-slate-900 border border-slate-700 w-10 text-center text-white rounded p-0.5" />
             </div>
             <div className="flex items-center justify-between gap-1 text-[10px] text-slate-400 mt-1">
                 <span>Catering</span>
                 <input type="checkbox" checked={p.hatCatering} onChange={(e) => updateParticipant(p.id, 'hatCatering', e.target.checked)} className="accent-[#E6C299]" />
             </div>
             <div className="flex items-center justify-between gap-1 text-[10px] text-slate-400 mt-1">
                 <span title="Netto-Rabatt in €">Rabatt €</span>
                 <input
                   type="number"
                   min="0"
                   step="1"
                   value={p.discountNetto || 0}
                   onChange={(e) => updateParticipant(p.id, 'discountNetto', Math.max(0, parseFloat(e.target.value) || 0))}
                   className="bg-slate-900 border border-slate-700 w-14 text-right text-[#E6C299] rounded p-0.5 font-mono"
                 />
             </div>
             {p.promoCode && (
               <div className="mt-1 text-[9px] font-mono font-bold text-emerald-300 bg-emerald-950/60 border border-emerald-500/30 rounded px-1 py-0.5 truncate" title={p.discountLabel || p.promoCode}>
                 Code: {p.promoCode}
               </div>
             )}
          </td>
          <td className="p-2 text-right align-top">
             <div className="text-[10px] font-mono text-emerald-400 mb-2 bg-emerald-900/20 px-1 py-0.5 rounded inline-block">{p.rechnungNr}</div>
             {p.invoiceMailSentAt ? (
               <div
                 className="text-[9px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 rounded mb-2 inline-block"
                 title={new Date(p.invoiceMailSentAt).toLocaleString('de-DE')}
               >
                 Mail versendet
               </div>
             ) : p.rechnungNr ? (
               <div className="text-[9px] font-black uppercase tracking-widest text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded mb-2 inline-block">
                 Mail offen
               </div>
             ) : null}
             <div className="flex items-center justify-end gap-1 mt-1">
                <button onClick={(e) => { e.stopPropagation(); downloadSinglePDF(index, p); }} className="text-blue-400 hover:text-white bg-blue-900/30 p-1.5 rounded transition-colors" title="Diese Einzel-PDF herunterladen"><Download className="w-3.5 h-3.5" /></button>
                <button
                  onClick={(e) => { e.stopPropagation(); void handleSendMail(p); }}
                  className={`p-1.5 rounded transition-colors ${p.invoiceMailSentAt ? 'text-amber-400 hover:text-white bg-amber-900/30' : 'text-emerald-400 hover:text-white bg-emerald-900/30'}`}
                  title={p.invoiceMailSentAt ? 'Rechnung erneut per E-Mail versenden' : 'Per E-Mail versenden'}
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { 
                    e.stopPropagation(); 
                    if(window.confirm('Achtung: Teilnehmer wirklich unwiderruflich löschen?')) {
                        removeParticipant(p.id); 
                    }
                }} className="text-red-400 hover:text-white bg-red-900/30 p-1.5 rounded transition-colors ml-2" title="Löschen"><Trash2 className="w-3.5 h-3.5" /></button>
             </div>
          </td>
        </tr>
      );
    });
  };

  // --- UI RENDER: DOC VIEW (ANTI-COLLAPSE TABELLEN ARCHITEKTUR) ---
  const InvoiceDocument: React.FC<{pData: any, sData: EventDoc, pNr: number}> = ({pData, sData, pNr}) => {
    const countryMap: {[key: string]: string} = { 'DE': 'Deutschland', 'AT': 'Österreich', 'CH': 'Schweiz', 'IT': 'Italien' };
    
    return (
        <div className="invoice-print-container" style={{ width: '794px', minHeight: '1123px', padding: '60px', boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#000000', fontFamily: 'Arial, sans-serif' }}>
            
            {/* KOPFBEREICH - Strikte, fixe Tabelle */}
            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '337px', verticalAlign: 'top', padding: '0', borderBottom: '2px solid #0f172a', paddingBottom: '20px' }}>
                            <h1 style={{ fontSize: '32px', margin: '0 0 5px 0', fontWeight: 'bold', color: '#0f172a' }}>RECHNUNG</h1>
                            <p style={{ color: '#d97706', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 20px 0' }}>Einmalige Leistung / Seminar</p>
                            
                            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                <p style={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', margin: '0 0 5px 0' }}>Rechnung an:</p>
                                {pData.participant.firma && <><strong style={{fontWeight: 'bold'}}>{pData.participant.firma}</strong><br /></>}
                                {pData.participant.vorname} {pData.participant.nachname}<br />
                                {pData.participant.strasse}<br />
                                {pData.participant.plz} {pData.participant.stadt}<br />
                                {countryMap[pData.participant.land.toUpperCase()] || pData.participant.land}
                            </div>
                        </td>
                        <td style={{ width: '337px', verticalAlign: 'top', textAlign: 'right', padding: '0', borderBottom: '2px solid #0f172a', paddingBottom: '20px' }}>
                            {/* Einfacher Div ohne opacity, inline-block etc. */}
                            <div style={{ backgroundColor: '#f8fafc', padding: '15px', border: '1px solid #e2e8f0', textAlign: 'right', fontSize: '12px', lineHeight: '1.4', color: '#475569', marginLeft: 'auto', width: '220px' }}>
                                <p style={{ fontWeight: 'bold', fontSize: '14px', color: '#000000', margin: '0 0 8px 0' }}>PreisExpert</p>
                                <p style={{ margin: '0' }}>Inhaber: Christian Funk</p>
                                <p style={{ margin: '0' }}>Rotehahnstraße 2</p>
                                <p style={{ margin: '0' }}>21335 Lüneburg</p>
                                <p style={{ color: '#d97706', fontWeight: 'bold', margin: '10px 0 0 0' }}>erfolg@friseur-coaching.de</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* DATUM UND NUMMER */}
            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '337px', padding: '15px' }}>
                            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Datum</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{pData.formattedDate}</span>
                        </td>
                        <td style={{ width: '337px', padding: '15px', textAlign: 'right' }}>
                            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Rechnungs-Nr.</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{pData.nr}</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* RECHNUNGSPOSTEN */}
            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr>
                        <th style={{ width: '474px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>Beschreibung / Leistung</th>
                        <th style={{ width: '60px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>MwSt.</th>
                        <th style={{ width: '140px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>Netto</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}>
                            <strong style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#020617' }}>{sData.title} {sData.location && `(Ort: ${sData.location})`}</strong>
                            <span style={{ fontSize: '13px', color: '#334155' }}>{sData.description}</span>
                        </td>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{pData.isReverseCharge ? '0%' : '19%'}</td>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'right', fontSize: '14px' }}>{formatCurrency(pData.baseNetto)}</td>
                    </tr>
                    {pData.participant.zusatzPersonen > 0 && (
                        <tr>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}><strong style={{ display: 'block', fontSize: '14px', color: '#020617' }}>Zusatzteilnehmer ({pData.participant.zusatzPersonen}x)</strong></td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{pData.isReverseCharge ? '0%' : '19%'}</td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'right', fontSize: '14px' }}>{formatCurrency(pData.zusatzNetto)}</td>
                        </tr>
                    )}
                    {pData.participant.hatCatering && (
                        <tr>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}><strong style={{ display: 'block', fontSize: '14px', color: '#020617' }}>Catering ({1 + pData.participant.zusatzPersonen}x)</strong></td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{pData.isReverseCharge ? '0%' : '19%'}</td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'right', fontSize: '14px' }}>{formatCurrency(pData.cateringNetto)}</td>
                        </tr>
                    )}
                    {pData.discountNetto > 0 && (
                        <tr>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}>
                                <strong style={{ display: 'block', fontSize: '14px', color: '#059669' }}>
                                    Rabatt / Einladungscode {pData.participant.promoCode ? `(${pData.participant.promoCode})` : ''}
                                </strong>
                                {pData.participant.discountLabel && (
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>{pData.participant.discountLabel}</span>
                                )}
                            </td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{pData.isReverseCharge ? '0%' : '19%'}</td>
                            <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'right', fontSize: '14px', color: '#059669', fontWeight: 'bold' }}>
                                −{formatCurrency(pData.discountNetto)}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* SUMMEN */}
            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '400px' }}></td>
                        <td style={{ width: '274px' }}>
                            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <tbody>
                                    <tr>
                                        <td style={{ width: '134px', padding: '5px 0', color: '#64748b' }}>Summe (Netto):</td>
                                        <td style={{ width: '140px', padding: '5px 0', textAlign: 'right' }}>{formatCurrency(pData.netto)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '5px 0', color: '#64748b', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>MwSt. {pData.isReverseCharge ? '(RC)' : '(19%)'}:</td>
                                        <td style={{ padding: '5px 0', textAlign: 'right', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>{formatCurrency(pData.vat)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '15px 0', fontWeight: 'bold', fontSize: '14px' }}>Gesamtbetrag:</td>
                                        <td style={{ padding: '15px 0', textAlign: 'right', color: '#d97706', fontSize: '18px', fontWeight: 'bold' }}>{formatCurrency(pData.brutto)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* ZAHLUNGSHINWEIS */}
            <div style={{ width: '674px', backgroundColor: '#f8fafc', padding: '20px', border: '1px solid #e2e8f0', boxSizing: 'border-box', fontSize: '12px', lineHeight: '1.5' }}>
                {pData.brutto <= 0 ? (
                    <p style={{ margin: '0', fontWeight: 'bold', color: '#059669' }}>
                        Kostenfreie Seminar-Einladung (100 % Rabatt) — es besteht kein offener Zahlungsbetrag (0,00 €).
                    </p>
                ) : (
                    <>
                        <p style={{ margin: '0 0 15px 0' }}>Bitte überweisen Sie den Gesamtbetrag unter Angabe der Rechnungsnummer <strong style={{ fontSize: '13px' }}>{pData.nr}</strong> bis zum <strong>{new Date(new Date().setDate(new Date().getDate() + 10)).toLocaleDateString('de-DE')}</strong>.</p>
                        
                        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: '1px solid #f1f5f9' }}>
                            <tbody>
                                <tr>
                                    <td style={{ width: '33%', padding: '10px', color: '#334155' }}>Kontoinhaber: Christian Funk</td>
                                    <td style={{ width: '45%', padding: '10px', color: '#334155' }}>IBAN: DE43 2406 0300 8523 1266 00</td>
                                    <td style={{ width: '22%', padding: '10px', color: '#334155' }}>BIC: GENODEF1NBU</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>
            
        </div>
    );
  };

  // --- VIEW: EVENT LISTE ---
  if (view === 'list') {
      return (
          <div className="p-4 md:p-8 font-sans bg-[#020617] text-slate-300 min-h-screen">
              <div className="flex items-center justify-between mb-8 border-b border-slate-800 pb-6">
                  <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#E6C299] rounded-xl flex items-center justify-center text-black font-black text-3xl shadow-lg">P</div>
                      <div>
                          <h1 className="text-3xl font-bold text-white tracking-tight">Event-<span className="text-[#E6C299]">Manager</span></h1>
                          <p className="text-slate-400">Verwalte Seminare, Teilnehmer und Rechnungen.</p>
                      </div>
                  </div>
                  <button
                      onClick={() => {
                          setNewEvent({
                              title: '',
                              date: '',
                              location: '',
                              locationAddress: '',
                              description: '',
                              priceBestand: 249,
                              priceExtern: 349,
                              priceZusatz: 149,
                              priceCatering: 49,
                              marketingTeaser: '',
                              marketingBullets: [''],
                              marketingPitch: '',
                              marketingImage: '',
                              isOnline: false,
                              onlineLink: '',
                              showPromotionPopup: false,
                              scheduleDays: [createEmptyScheduleDay()],
                          });
                          setView('create');
                      }}
                      className="px-5 py-3 bg-[#E6C299] hover:bg-white text-black rounded-lg text-sm font-black uppercase tracking-widest flex items-center gap-2 transition-colors shadow"
                  >
                      <Plus className="w-4 h-4" /> Neues Event Planen
                  </button>
              </div>

              <div className="flex gap-2 mb-6">
                  <button
                      type="button"
                      onClick={() => setListTab('active')}
                      className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${listTab === 'active' ? 'bg-[#E6C299] text-black border-[#E6C299]' : 'bg-slate-900 text-slate-400 border-slate-700'}`}
                  >
                      Aktuell ({activeEvents.length})
                  </button>
                  <button
                      type="button"
                      onClick={() => setListTab('archive')}
                      className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${listTab === 'archive' ? 'bg-slate-600 text-white border-slate-500' : 'bg-slate-900 text-slate-400 border-slate-700'}`}
                  >
                      Archiv ({archivedEventsList.length})
                  </button>
                  {Object.values(pendingRegsByEventId).reduce((a, b) => a + b, 0) > 0 && (
                    <span className="ml-auto self-center inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-orange-300 animate-pulse">
                      {Object.values(pendingRegsByEventId).reduce((a, b) => a + b, 0)} neue Anmeldung(en) — Event öffnen
                    </span>
                  )}
              </div>
              {listTab === 'archive' && (
                  <p className="text-xs text-slate-500 mb-4">
                      Abgelaufene Seminare landen automatisch hier. Teilnehmer &amp; Rechnungen bleiben vollständig abrufbar.
                  </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {visibleEvents.map(ev => {
                      const landingOk = publicEventIds.has(ev.id!);
                      const pendingCount = pendingRegsByEventId[ev.id!] || 0;
                      const unsentInvoices = (ev.participants || []).filter(
                        (p) => p.rechnungNr && !p.invoiceMailSentAt
                      ).length;
                      return (
                      <div key={ev.id} onClick={() => { setActiveEvent(ev); setView('detail'); }} className={`bg-[#0f172a] border ${pendingCount > 0 ? 'border-orange-500/60 ring-1 ring-orange-500/20' : ev.isArchived ? 'border-slate-700 opacity-90' : landingOk ? 'border-slate-800 hover:border-[#E6C299]/50' : 'border-red-500/50 hover:border-red-400'} rounded-2xl p-6 cursor-pointer group transition-all shadow-xl relative`}>
                          {pendingCount > 0 && (
                              <span className="absolute -top-2 -right-2 min-w-[1.5rem] h-6 px-2 rounded-full bg-orange-500 text-black text-[11px] font-black flex items-center justify-center animate-pulse shadow-lg z-10">
                                  {pendingCount}
                              </span>
                          )}
                          <div className="flex justify-between items-start mb-4 gap-2">
                              <h3 className="text-xl font-bold text-white group-hover:text-[#E6C299] transition-colors pr-2">{ev.title}</h3>
                              <div className="flex items-center gap-2 shrink-0">
                                  <Calendar className="text-slate-600 group-hover:text-[#E6C299]" />
                                  {listTab === 'archive' && (
                                      <>
                                      <button
                                          type="button"
                                          title="Als Vorlage für ein neues Seminar verwenden"
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleUseArchiveAsTemplate(ev);
                                          }}
                                          className="px-2 py-1.5 rounded-lg bg-[#E6C299]/15 text-[#E6C299] hover:bg-[#E6C299] hover:text-black border border-[#E6C299]/40 text-[9px] font-black uppercase tracking-widest flex items-center gap-1"
                                      >
                                          <Copy className="w-3 h-3" /> Vorlage
                                      </button>
                                      <button
                                          type="button"
                                          title="Aus Archiv zurückholen"
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              void handleRestoreFromArchive(ev);
                                          }}
                                          className="px-2 py-1.5 rounded-lg bg-emerald-900/30 text-emerald-400 hover:bg-emerald-600 hover:text-white border border-emerald-900/40 text-[9px] font-black uppercase tracking-widest"
                                      >
                                          Restore
                                      </button>
                                      </>
                                  )}
                                  <button
                                      type="button"
                                      title="Event löschen"
                                      disabled={isDeletingEvent}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          void handleDeleteEvent(ev);
                                      }}
                                      className="p-2 rounded-lg bg-red-900/20 text-red-400 hover:bg-red-600 hover:text-white border border-red-900/40 transition-colors"
                                  >
                                      <Trash2 className="w-4 h-4" />
                                  </button>
                              </div>
                          </div>
                          {ev.isArchived && (
                              <div className="mb-3 text-[10px] font-black uppercase tracking-widest text-slate-300 bg-slate-800/80 border border-slate-600 rounded-lg px-3 py-2">
                                  Archiviert — Rechnungen weiterhin öffnen
                              </div>
                          )}
                          {!ev.isArchived && !landingOk && (
                              <div className="mb-3 text-[10px] font-black uppercase tracking-widest text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">
                                  Landingpage kaputt / fehlt — bitte löschen oder Event speichern
                              </div>
                          )}
                          <div className="text-sm text-slate-400 mb-2 space-y-0.5">
                            {(() => {
                              const days = sortScheduleDays(normalizeScheduleDays(ev.scheduleDays));
                              if (days.length > 0) {
                                return days.map((d) => (
                                  <div key={d.id}>{formatScheduleDayLine(d)}</div>
                                ));
                              }
                              return <div>{ev.date || '—'}</div>;
                            })()}
                          </div>
                          <div className="text-xs text-slate-500 mb-4">{ev.location || 'Kein Ort angegeben'}</div>
                          <div className="flex justify-between items-center pt-4 border-t border-slate-800 gap-2">
                              <div className="flex flex-wrap gap-1.5">
                                  <span className="text-xs font-bold bg-slate-800 px-2.5 py-1 rounded text-slate-300">{(ev.participants || []).length} Anmeldungen</span>
                                  <span className="text-xs font-bold bg-slate-800 px-2.5 py-1 rounded text-[#E6C299]">{(ev.participants || []).reduce((n, p) => n + getParticipantHeadcount(p), 0)} Teilnehmer</span>
                                  {pendingCount > 0 && (
                                    <span className="text-xs font-bold bg-orange-500/20 border border-orange-500/40 px-2.5 py-1 rounded text-orange-300">
                                      {pendingCount} neu
                                    </span>
                                  )}
                                  {unsentInvoices > 0 && (
                                    <span className="text-xs font-bold bg-amber-500/15 border border-amber-500/30 px-2.5 py-1 rounded text-amber-300">
                                      {unsentInvoices} Mail offen
                                    </span>
                                  )}
                              </div>
                              <ArrowRight className="text-slate-600 group-hover:text-white transition-colors w-5 h-5 shrink-0" />
                          </div>
                      </div>
                      );
                  })}
                  {visibleEvents.length === 0 && (
                      <div className="col-span-full text-center text-slate-500 p-10 italic">
                          {listTab === 'archive' ? 'Archiv ist leer.' : 'Noch keine Events angelegt.'}
                      </div>
                  )}
              </div>
          </div>
      );
  }

  // --- VIEW: EVENT ERSTELLEN ODER BEARBEITEN ---
  if (view === 'create' || view === 'edit') {
      return (
          <div className="p-4 md:p-8 font-sans bg-[#020617] text-slate-300 min-h-screen">
              <button onClick={() => setView(view === 'edit' ? 'detail' : 'list')} className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"><ArrowLeft className="w-4 h-4" /> Zurück</button>
              <div className="max-w-3xl bg-[#0f172a] p-8 rounded-2xl border border-slate-800 shadow-xl">
                  <h2 className="text-2xl font-bold text-white mb-2 border-b border-slate-800 pb-4">{view === 'edit' ? 'Event bearbeiten' : 'Event Parameter definieren'}</h2>
                  {view === 'create' && !newEvent.date && (newEvent.title || newEvent.location) && (
                      <p className="text-xs text-[#E6C299] mb-4 bg-[#E6C299]/10 border border-[#E6C299]/25 rounded-lg px-3 py-2">
                        Vorlage geladen — bitte vor allem <strong>Datum</strong> (und ggf. Titel) setzen, dann speichern. Teilnehmer &amp; Rechnungen starten leer.
                      </p>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <AdminInput label="Titel (z.B. Seminar Q3)" value={newEvent.title || ''} onChange={(e:any) => setNewEvent({...newEvent, title: e.target.value})} />
                      </div>
                      <div className="md:col-span-2 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                            Seminartage &amp; Uhrzeiten
                          </label>
                          <button
                            type="button"
                            onClick={() =>
                              setNewEvent({
                                ...newEvent,
                                scheduleDays: [
                                  ...(newEvent.scheduleDays || []),
                                  createEmptyScheduleDay(),
                                ],
                              })
                            }
                            className="text-[10px] font-black uppercase tracking-widest text-[#E6C299] hover:text-white flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> Tag hinzufügen
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Pro Tag Datum + von/bis — erscheint so auf der Landingpage (z.B. Sonntag 09:00–17:00, Montag 09:00–15:00).
                        </p>
                        {(newEvent.scheduleDays || []).map((day, index) => {
                          const weekday = day.date ? formatGermanWeekday(day.date) : '';
                          return (
                            <div
                              key={day.id || index}
                              className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end bg-black/40 border border-slate-800 rounded-xl p-3"
                            >
                              <div>
                                <label className="text-[9px] uppercase text-slate-500 font-bold block mb-1">
                                  Tag {index + 1}{weekday ? ` · ${weekday}` : ''}
                                </label>
                                <input
                                  type="date"
                                  value={day.date || ''}
                                  onChange={(e) => {
                                    const next = [...(newEvent.scheduleDays || [])];
                                    next[index] = { ...day, date: e.target.value };
                                    const summary = buildDateSummaryFromDays(normalizeScheduleDays(next));
                                    setNewEvent({
                                      ...newEvent,
                                      scheduleDays: next,
                                      date: summary || newEvent.date,
                                    });
                                  }}
                                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E6C299]"
                                />
                              </div>
                              <div>
                                <label className="text-[9px] uppercase text-slate-500 font-bold block mb-1">Von</label>
                                <input
                                  type="time"
                                  value={day.timeFrom || ''}
                                  onChange={(e) => {
                                    const next = [...(newEvent.scheduleDays || [])];
                                    next[index] = { ...day, timeFrom: e.target.value };
                                    setNewEvent({ ...newEvent, scheduleDays: next });
                                  }}
                                  className="w-full sm:w-[7.5rem] bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E6C299]"
                                />
                              </div>
                              <div>
                                <label className="text-[9px] uppercase text-slate-500 font-bold block mb-1">Bis</label>
                                <input
                                  type="time"
                                  value={day.timeTo || ''}
                                  onChange={(e) => {
                                    const next = [...(newEvent.scheduleDays || [])];
                                    next[index] = { ...day, timeTo: e.target.value };
                                    setNewEvent({ ...newEvent, scheduleDays: next });
                                  }}
                                  className="w-full sm:w-[7.5rem] bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E6C299]"
                                />
                              </div>
                              <button
                                type="button"
                                disabled={(newEvent.scheduleDays || []).length <= 1}
                                onClick={() => {
                                  const next = (newEvent.scheduleDays || []).filter((_, i) => i !== index);
                                  const summary = buildDateSummaryFromDays(normalizeScheduleDays(next));
                                  setNewEvent({
                                    ...newEvent,
                                    scheduleDays: next.length ? next : [createEmptyScheduleDay()],
                                    date: summary || newEvent.date,
                                  });
                                }}
                                className="h-[38px] px-3 rounded-lg border border-red-900/50 text-red-400 hover:bg-red-900/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                                title="Tag entfernen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <AdminInput
                        label="Datums-Kurztext (Rechnung / Übersicht, wird aus Tagen befüllt)"
                        type="text"
                        placeholder="21. & 22.06.2026"
                        value={newEvent.date || ''}
                        onChange={(e: any) => setNewEvent({ ...newEvent, date: e.target.value })}
                      />
                      <AdminInput label="Ort (Kurzname für die Rechnung, z.B. Wolpertshausen)" value={newEvent.location || ''} onChange={(e:any) => setNewEvent({...newEvent, location: e.target.value})} />
                      <AdminInput label="Genaue Adresse (Wird in der E-Mail angezeigt)" placeholder="Hotel XYZ, Musterstraße 1, 12345 Stadt" value={newEvent.locationAddress || ''} onChange={(e:any) => setNewEvent({...newEvent, locationAddress: e.target.value})} />
                  </div>
                  <div className="mt-4">
                      <label className="flex items-center gap-3 cursor-pointer group">
                          <input
                              type="checkbox"
                              checked={newEvent.isOnline || false}
                              onChange={(e) => setNewEvent({ ...newEvent, isOnline: e.target.checked, onlineLink: e.target.checked ? (newEvent.onlineLink || '') : '' })}
                              className="accent-[#E6C299] w-4 h-4 shrink-0"
                          />
                          <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">Online-Seminar</span>
                      </label>
                      {newEvent.isOnline && (
                          <div className="mt-3">
                              <AdminInput
                                  label="Online-Teilnahme-Link (Zoom/Teams)"
                                  type="url"
                                  placeholder="https://zoom.us/j/..."
                                  value={newEvent.onlineLink || ''}
                                  onChange={(e: any) => setNewEvent({ ...newEvent, onlineLink: e.target.value })}
                              />
                          </div>
                      )}
                      <label className="flex items-center gap-3 cursor-pointer group mt-4">
                          <input
                              type="checkbox"
                              checked={newEvent.showPromotionPopup === true}
                              onChange={(e) => setNewEvent({ ...newEvent, showPromotionPopup: e.target.checked })}
                              className="accent-[#E6C299] w-4 h-4 shrink-0"
                          />
                          <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                              In-App Popup-Promotion aktivieren
                          </span>
                      </label>
                      {newEvent.showPromotionPopup === true && (
                          <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                              Das Popup erscheint bei Mandanten nur, wenn das Seminar in maximal 14 Tagen stattfindet.
                          </p>
                      )}
                  </div>
                  <AdminInput label="Rechnungs-Beschreibung" textarea value={newEvent.description || ''} onChange={(e:any) => setNewEvent({...newEvent, description: e.target.value})} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-black p-5 rounded-xl border border-slate-800 mt-4">
                      <div><label className="text-[10px] uppercase text-emerald-400 block mb-1">Netto (Bestand)</label><input type="number" value={newEvent.priceBestand} onChange={(e) => setNewEvent({...newEvent, priceBestand: parseFloat(e.target.value)})} className="w-full bg-transparent text-white font-mono text-lg outline-none" /></div>
                      <div><label className="text-[10px] uppercase text-amber-400 block mb-1">Netto (Extern)</label><input type="number" value={newEvent.priceExtern} onChange={(e) => setNewEvent({...newEvent, priceExtern: parseFloat(e.target.value)})} className="w-full bg-transparent text-white font-mono text-lg outline-none" /></div>
                      <div><label className="text-[10px] uppercase text-blue-400 block mb-1">Preis Zusatz-P.</label><input type="number" value={newEvent.priceZusatz} onChange={(e) => setNewEvent({...newEvent, priceZusatz: parseFloat(e.target.value)})} className="w-full bg-transparent text-white font-mono text-lg outline-none" /></div>
                      <div><label className="text-[10px] uppercase text-pink-400 block mb-1">Catering p.P.</label><input type="number" value={newEvent.priceCatering} onChange={(e) => setNewEvent({...newEvent, priceCatering: parseFloat(e.target.value)})} className="w-full bg-transparent text-white font-mono text-lg outline-none" /></div>
                  </div>

                  {/* RABATT- & EINLADUNGSCODES */}
                  <div className="mt-6 bg-black/60 p-5 rounded-xl border border-slate-800 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                              <h4 className="text-sm font-bold text-[#E6C299] uppercase tracking-wider">
                                  Rabatt- &amp; Einladungscodes (optional)
                              </h4>
                              <p className="text-xs text-slate-400 mt-0.5">
                                  Mandanten / Teilnehmer können diesen Code bei der Anmeldung eingeben (z. B. 100 % kostenlos = 0 €, fester €-Abzug oder %-Rabatt).
                              </p>
                          </div>
                          <button
                              type="button"
                              onClick={() =>
                                  setNewEvent({
                                      ...newEvent,
                                      promoCodes: [...(newEvent.promoCodes || []), createEmptyPromoCode()],
                                  })
                              }
                              className="px-3 py-2 bg-[#E6C299]/15 hover:bg-[#E6C299] text-[#E6C299] hover:text-black border border-[#E6C299]/40 rounded-lg text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 transition-colors"
                          >
                              <Plus className="w-3.5 h-3.5" /> Code hinzufügen
                          </button>
                      </div>

                      {(newEvent.promoCodes || []).length === 0 ? (
                          <p className="text-xs text-slate-500 italic">
                              Keine Rabattcodes hinterlegt.
                          </p>
                      ) : (
                          <div className="space-y-3">
                              {(newEvent.promoCodes || []).map((promo, idx) => {
                                  const updatePromoAt = (patch: Partial<SeminarPromoCode>) => {
                                      const list = [...(newEvent.promoCodes || [])];
                                      list[idx] = { ...list[idx], ...patch };
                                      setNewEvent({ ...newEvent, promoCodes: list });
                                  };
                                  const removePromoAt = () => {
                                      const list = (newEvent.promoCodes || []).filter((_, i) => i !== idx);
                                      setNewEvent({ ...newEvent, promoCodes: list });
                                  };

                                  return (
                                      <div
                                          key={promo.id || idx}
                                          className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center bg-slate-900/90 border border-slate-700/80 rounded-xl p-3"
                                      >
                                          <div className="md:col-span-3">
                                              <label className="text-[10px] uppercase text-slate-400 block mb-1">
                                                  Code (z.B. VIP100)
                                              </label>
                                              <input
                                                  type="text"
                                                  value={promo.code}
                                                  onChange={(e) =>
                                                      updatePromoAt({
                                                          code: normalizePromoCodeString(e.target.value),
                                                      })
                                                  }
                                                  placeholder="GAST2026"
                                                  className="w-full bg-black border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm uppercase focus:outline-none focus:border-[#E6C299]"
                                              />
                                          </div>

                                          <div className="md:col-span-4">
                                              <label className="text-[10px] uppercase text-slate-400 block mb-1">
                                                  Rabatt-Art
                                              </label>
                                              <select
                                                  value={promo.type}
                                                  onChange={(e) => {
                                                      const nextType = e.target.value as SeminarPromoCodeType;
                                                      updatePromoAt({
                                                          type: nextType,
                                                          value: nextType === 'free' ? 100 : promo.value,
                                                      });
                                                  }}
                                                  className="w-full bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E6C299]"
                                              >
                                                  <option value="free">100 % Kostenlos (Auf 0,00 € setzen)</option>
                                                  <option value="fixed">Fester Euro-Abzug (netto €)</option>
                                                  <option value="percent">Prozent-Rabatt (%)</option>
                                              </select>
                                          </div>

                                          <div className="md:col-span-2">
                                              <label className="text-[10px] uppercase text-slate-400 block mb-1">
                                                  {promo.type === 'free'
                                                      ? 'Wert'
                                                      : promo.type === 'percent'
                                                        ? 'Rabatt in %'
                                                        : 'Abzug netto €'}
                                              </label>
                                              <input
                                                  type="number"
                                                  min="0"
                                                  max={promo.type === 'percent' ? 100 : 10000}
                                                  step={promo.type === 'percent' ? 5 : 10}
                                                  disabled={promo.type === 'free'}
                                                  value={promo.type === 'free' ? 100 : promo.value}
                                                  onChange={(e) =>
                                                      updatePromoAt({
                                                          value: Math.max(0, parseFloat(e.target.value) || 0),
                                                      })
                                                  }
                                                  className="w-full bg-black border border-slate-700 rounded-lg px-3 py-2 text-[#E6C299] font-mono text-sm disabled:opacity-40 focus:outline-none focus:border-[#E6C299]"
                                              />
                                          </div>

                                          <div className="md:col-span-2 flex items-center pt-4">
                                              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                                                  <input
                                                      type="checkbox"
                                                      checked={promo.applyToExtras === true}
                                                      onChange={(e) =>
                                                          updatePromoAt({ applyToExtras: e.target.checked })
                                                      }
                                                      className="accent-[#E6C299] w-4 h-4"
                                                  />
                                                  <span>inkl. Zusatz-P.</span>
                                              </label>
                                          </div>

                                          <div className="md:col-span-1 flex justify-end pt-4">
                                              <button
                                                  type="button"
                                                  onClick={removePromoAt}
                                                  className="p-2 bg-red-900/30 text-red-400 hover:text-white rounded-lg border border-red-900/50"
                                                  title="Code löschen"
                                              >
                                                  <Trash2 className="w-4 h-4" />
                                              </button>
                                          </div>
                                      </div>
                                  );
                              })}
                          </div>
                      )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-800">
                      <h3 className="text-lg font-bold text-white mb-4 text-[#E6C299]">Landingpage & Marketing Daten</h3>

                      <div className="mb-4">
                          <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Hero-Bild (Netflix-Hintergrund)</label>
                          <input type="file" id="marketing-image-upload" accept="image/*" onChange={handleMarketingImageUpload} className="hidden" />
                          <button
                              type="button"
                              onClick={() => document.getElementById('marketing-image-upload')?.click()}
                              disabled={isUploadingImage}
                              className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-[#E6C299] border border-slate-700 hover:border-[#E6C299]/50 rounded-lg text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors disabled:opacity-50"
                          >
                              {isUploadingImage ? (
                                  <><Loader2 className="w-4 h-4 animate-spin" /> Wird hochgeladen…</>
                              ) : (
                                  <><Upload className="w-4 h-4" /> Hero-Bild hochladen</>
                              )}
                          </button>
                          {newEvent.marketingImage && (
                              <img
                                  src={newEvent.marketingImage}
                                  alt="Hero-Vorschau"
                                  className="mt-3 rounded-xl border border-slate-700 max-h-48 w-full object-cover"
                              />
                          )}
                      </div>

                      <div className="mb-4">
                          <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Kurz-Teaser (1-2 Sätze für den Header)</label>
                          <textarea
                              rows={getMarketingTextareaRows(newEvent.marketingTeaser || '')}
                              value={newEvent.marketingTeaser || ''}
                              onChange={(e) => setNewEvent({...newEvent, marketingTeaser: e.target.value})}
                              placeholder="Kurzer Teaser für die Landingpage"
                              className={`${MARKETING_TEXTAREA_CLASSES} min-h-[4.5rem]`}
                          />
                      </div>

                      <div className="mb-4">
                          <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Agenda / Highlight Bullets</label>
                          {(newEvent.marketingBullets || ['']).map((bullet, index) => {
                              const bullets = newEvent.marketingBullets || [''];
                              const canMoveUp = index > 0;
                              const canMoveDown = index < bullets.length - 1;
                              const moveBullet = (from: number, to: number) => {
                                  if (to < 0 || to >= bullets.length) return;
                                  const next = [...bullets];
                                  const [item] = next.splice(from, 1);
                                  next.splice(to, 0, item);
                                  setNewEvent({ ...newEvent, marketingBullets: next });
                              };
                              return (
                              <div key={index} className="flex gap-2 mb-2 items-start">
                                  <textarea
                                      rows={getMarketingTextareaRows(bullet)}
                                      value={bullet}
                                      onChange={(e) => {
                                          const newBullets = [...(newEvent.marketingBullets || [])];
                                          newBullets[index] = e.target.value;
                                          setNewEvent({...newEvent, marketingBullets: newBullets});
                                      }}
                                      placeholder={`Agenda Punkt ${index + 1}`}
                                      className={`${MARKETING_TEXTAREA_CLASSES} min-h-[4.5rem]`}
                                  />
                                  <div className="flex flex-col gap-1 shrink-0">
                                      <button
                                          type="button"
                                          onClick={() => moveBullet(index, index - 1)}
                                          disabled={!canMoveUp}
                                          title="Nach oben"
                                          aria-label="Punkt nach oben verschieben"
                                          className="px-2.5 py-2 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg border border-slate-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-slate-800 disabled:hover:text-slate-300"
                                      >
                                          <ChevronUp className="w-4 h-4" />
                                      </button>
                                      <button
                                          type="button"
                                          onClick={() => moveBullet(index, index + 1)}
                                          disabled={!canMoveDown}
                                          title="Nach unten"
                                          aria-label="Punkt nach unten verschieben"
                                          className="px-2.5 py-2 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg border border-slate-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-slate-800 disabled:hover:text-slate-300"
                                      >
                                          <ChevronDown className="w-4 h-4" />
                                      </button>
                                      <button
                                          type="button"
                                          onClick={() => {
                                              const newBullets = (newEvent.marketingBullets || []).filter((_, i) => i !== index);
                                              setNewEvent({...newEvent, marketingBullets: newBullets.length ? newBullets : ['']});
                                          }}
                                          title="Löschen"
                                          aria-label="Punkt löschen"
                                          className="px-2.5 py-2 bg-red-900/30 text-red-400 hover:text-white rounded-lg border border-red-900/50"
                                      >
                                          <Trash2 className="w-4 h-4" />
                                      </button>
                                  </div>
                              </div>
                              );
                          })}
                          <button
                              onClick={() => setNewEvent({...newEvent, marketingBullets: [...(newEvent.marketingBullets || []), '']})}
                              className="text-xs font-bold text-blue-400 flex items-center gap-1 mt-2 hover:text-white"
                          >
                              <Plus className="w-3 h-3" /> Weiteren Punkt hinzufügen
                          </button>
                      </div>

                      <div className="mb-4">
                          <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Ausführlicher Sales-Pitch / Beschreibung</label>
                          <textarea
                              rows={getMarketingTextareaRows(newEvent.marketingPitch || '', 4)}
                              value={newEvent.marketingPitch || ''}
                              onChange={(e) => setNewEvent({...newEvent, marketingPitch: e.target.value})}
                              placeholder="Ausführliche Beschreibung für die Landingpage"
                              className={`${MARKETING_TEXTAREA_CLASSES} min-h-[8rem]`}
                          />
                      </div>
                  </div>

                  {view === 'create' ? (
                      <button onClick={handleCreateEvent} disabled={isSaving} className="mt-8 w-full bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest py-4 rounded-xl transition-colors">{isSaving ? 'Speichere...' : 'Event & Arbeitsraum erstellen'}</button>
                  ) : (
                      <button onClick={handleUpdateEvent} disabled={isSaving} className="mt-8 w-full bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest py-4 rounded-xl transition-colors flex justify-center items-center gap-2"><Save className="w-5 h-5"/> {isSaving ? 'Speichere...' : 'Änderungen speichern'}</button>
                  )}
              </div>
          </div>
      );
  }

  // --- VIEW: EVENT DETAIL (COCKPIT) ---
  return (
    <div className="admin-wrapper p-4 md:p-8 font-sans bg-[#020617] text-slate-300 min-h-screen relative z-10">
      <div className="flex items-center justify-between mb-8 border-b border-slate-800 pb-6 no-print">
        <div>
            <button onClick={() => { setActiveEvent(null); setView('list'); }} className="mb-2 flex items-center gap-2 text-slate-400 hover:text-[#E6C299] transition-colors text-xs font-bold uppercase tracking-widest"><ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht</button>
            <div className="flex items-center gap-4">
                <h1 className="text-3xl font-bold text-white tracking-tight">{activeEvent?.title} <span className="text-slate-600 text-lg ml-2 font-mono">({activeEvent?.date})</span></h1>
                
                <button 
                    onClick={() => {
                        setNewEvent({
                            ...activeEvent!,
                            promoCodes: normalizePromoCodes(activeEvent!.promoCodes),
                            marketingBullets: activeEvent!.marketingBullets?.length ? activeEvent!.marketingBullets : [''],
                            isOnline: activeEvent!.isOnline || false,
                            onlineLink: activeEvent!.onlineLink || '',
                            showPromotionPopup: activeEvent!.showPromotionPopup === true,
                            scheduleDays: normalizeScheduleDays(activeEvent!.scheduleDays).length
                              ? normalizeScheduleDays(activeEvent!.scheduleDays)
                              : [createEmptyScheduleDay()],
                        });
                        setView('edit');
                    }} 
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-[#E6C299] rounded-lg transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest border border-slate-700" 
                    title="Event-Daten bearbeiten"
                >
                    <Edit3 className="w-4 h-4" /> Event bearbeiten
                </button>
                {activeEvent?.id && !publicEventIds.has(activeEvent.id) && (
                    <span className="px-3 py-1.5 bg-red-950/50 text-red-400 rounded-lg text-[10px] font-black uppercase tracking-widest border border-red-500/40">
                        Landing fehlt
                    </span>
                )}
                {activeEvent?.id && (
                    <a
                        href={window.location.origin + '/seminar/' + activeEvent.id}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const url = window.location.origin + '/seminar/' + activeEvent.id;
                            // Full document load → index.tsx mountet PublicAppShell (kein App/Auth/CRM)
                            const win = window.open(url, '_blank');
                            if (!win) {
                                window.location.assign(url);
                            }
                        }}
                        className="px-3 py-1.5 bg-[#E6C299]/10 hover:bg-[#E6C299] text-[#E6C299] hover:text-black rounded-lg transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest border border-[#E6C299]/30"
                        title="Landingpage-Vorschau in neuem Tab"
                    >
                        <ExternalLink className="w-4 h-4" /> Landingpage
                    </a>
                )}
                {activeEvent?.id && (
                    <button
                        type="button"
                        disabled={isDeletingEvent}
                        onClick={() => { void handleDeleteEvent(activeEvent); }}
                        className="px-3 py-1.5 bg-red-900/20 hover:bg-red-600 text-red-400 hover:text-white rounded-lg transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest border border-red-900/40"
                        title="Event inkl. Landingpage löschen"
                    >
                        <Trash2 className="w-4 h-4" /> {isDeletingEvent ? 'Lösche…' : 'Event löschen'}
                    </button>
                )}
                {activeEvent?.isArchived && (
                    <button
                        type="button"
                        onClick={() => handleUseArchiveAsTemplate(activeEvent)}
                        className="px-3 py-1.5 bg-[#E6C299]/15 hover:bg-[#E6C299] text-[#E6C299] hover:text-black rounded-lg transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest border border-[#E6C299]/40"
                        title="Als Vorlage für ein neues Seminar (Archiv bleibt)"
                    >
                        <Copy className="w-4 h-4" /> Als Vorlage
                    </button>
                )}
            </div>
            {activeEvent?.location && <p className="text-slate-400 mt-1 text-sm flex items-center gap-2">📍 {activeEvent.location}</p>}
        </div>
        <div className="flex gap-3">
             <input type="file" id="csv-upload" accept=".csv" onChange={handleFileUpload} className="hidden" />
             <button onClick={() => document.getElementById('csv-upload')?.click()} className="px-4 py-2.5 bg-blue-600/20 text-blue-400 border border-blue-600/30 hover:bg-blue-600 hover:text-white rounded-lg text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition">
                <Upload className="w-4 h-4" /> CSV Liste importieren
             </button>
        </div>
      </div>

      <section className="mb-8">
        <h3 className="text-xl font-bold text-[#E6C299] mb-4 mt-8 flex items-center gap-2">
          📥 Neue Anmeldungen (Puffer)
          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{bufferedRegistrations.length}</span>
        </h3>

        {needsConfirmationRegs.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 bg-amber-950/30 border border-amber-600/40 rounded-xl p-4">
            <p className="text-amber-200 text-sm flex-1 min-w-[200px]">
              <span className="font-bold">{needsConfirmationRegs.length}</span> bereits verarbeitete Anmeldung(en) ohne Bestätigungsmail.
            </p>
            <button
              type="button"
              onClick={() => { void handleResendConfirmationMails(); }}
              disabled={isResendingConfirmations}
              className="shrink-0 px-4 py-2 bg-amber-500/20 hover:bg-amber-500 text-amber-200 hover:text-black border border-amber-500/40 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
            >
              {isResendingConfirmations ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sende…</span>
              ) : (
                'Bestätigung nachsenden'
              )}
            </button>
          </div>
        )}

        {bufferedRegistrations.length === 0 ? (
          <p className="text-slate-500 text-sm italic">Aktuell keine neuen Anmeldungen.</p>
        ) : (
          <div className="space-y-3">
            {bufferedRegistrations.map((registration) => {
              const formData = registration.formData || {};
              const financials = registration.financials || {};
              const createdAt = registration.createdAt?.toDate
                ? registration.createdAt.toDate().toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—';

              return (
                <div
                  key={registration.id}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                >
                  <div className="space-y-1 text-sm">
                    <p className="text-slate-500 text-xs">{createdAt}</p>
                    <p className="text-white font-semibold">
                      {formData.vorname} {formData.nachname}
                    </p>
                    <p className="text-slate-400">{formData.firma}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 pt-1">
                      <span>Mandant: {formData.isMandant ? 'Ja' : 'Nein'}</span>
                      <span>Brutto: {formatCurrency(financials.bruttoGesamt || 0)}</span>
                      <span>Zusatz: {formData.zusatzPersonen ?? 0}</span>
                      {(financials.promoCode || formData.promoCode) && (
                        <span className="text-emerald-400 font-mono font-bold">
                          Code: {financials.promoCode || formData.promoCode} (−{formatCurrency(financials.discountNetto || 0)} netto)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleProcessRegistration(registration)}
                    disabled={processingRegistrationId !== null}
                    className="shrink-0 px-4 py-2 bg-[#E6C299]/10 hover:bg-[#E6C299] text-[#E6C299] hover:text-black border border-[#E6C299]/30 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {processingRegistrationId === registration.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Wird verarbeitet…
                      </>
                    ) : (
                      'Anmeldung verarbeiten'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {activeEvent?.id && (
        <SeminarDinnerManager
          seminarId={activeEvent.id}
          seminarTitle={activeEvent.title || ''}
          participants={activeEvent.participants || []}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="control-panel">
          <div className="bg-[#0f172a] p-6 rounded-2xl border border-slate-800 shadow-xl">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Users className="text-[#E6C299]"/> Teilnehmer-Verwaltung</h2>
                <button onClick={addManualRow} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1 transition">
                  <Plus className="w-3 h-3" /> Manuell
                </button>
            </div>

            {csvError && <div className="bg-red-950 border border-red-500 text-red-200 p-4 rounded-lg mb-4 text-sm font-medium flex justify-between items-center">
                <span>{csvError}</span>
                <button onClick={() => setCsvError(null)}><X className="w-4 h-4" /></button>
            </div>}

            <div className="overflow-x-auto max-h-[600px] border border-slate-800 rounded-lg bg-black/40">
              <table className="w-full text-sm border-collapse text-slate-300 font-sans">
                <thead className="bg-black sticky top-0 z-10 shadow-md">
                  <tr className="text-left text-[10px] text-slate-500 uppercase tracking-wider">
                    <th className="p-3 w-1/3">Name & E-Mail</th>
                    <th className="p-3 w-1/3">Adresse (PLZ/Ort zwingend prüfen!)</th>
                    <th className="p-3 text-center">Status & Optionen</th>
                    <th className="p-3 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {activeEvent?.participants.length ? renderTableRows() : <tr><td colSpan={4} className="p-10 text-center text-slate-600 italic">Noch keine Teilnehmer in dieser Akte.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-[#0f172a] p-6 rounded-2xl border border-slate-800 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Kosten & Ausgaben</h2>
                <button onClick={addCostRow} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1 transition">
                  <Plus className="w-3 h-3" /> Kostenpunkt hinzufügen
                </button>
              </div>
              <p className="text-slate-500 text-xs mb-4">Bitte nur Netto-Werte eingeben (keine Brutto-Beträge).</p>
              <div className="space-y-2">
                {(activeEvent?.costs || []).length === 0 ? (
                  <p className="text-slate-600 italic text-sm p-4 text-center border border-dashed border-slate-800 rounded-lg">Noch keine Kosten erfasst.</p>
                ) : (
                  (activeEvent?.costs || []).map((cost) => (
                    <div key={cost.id} className="flex items-center gap-2 bg-black/40 border border-slate-800 rounded-lg p-2">
                      <input
                        type="text"
                        value={cost.description}
                        onChange={(e) => updateCost(cost.id, 'description', e.target.value)}
                        placeholder="Beschreibung (z.B. Location, Catering)"
                        className="bg-transparent border-b border-slate-700 flex-1 text-white focus:outline-none focus:border-[#E6C299] text-xs p-1"
                      />
                      <CostAmountInput
                        amount={cost.amount}
                        onCommit={(amount) => updateCost(cost.id, 'amount', amount)}
                      />
                      <button
                        onClick={() => {
                          if (window.confirm('Kostenpunkt wirklich löschen?')) removeCost(cost.id);
                        }}
                        className="text-red-400 hover:text-white bg-red-900/30 p-1.5 rounded transition-colors"
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 💉 CHIRURGIE-FIX: FINANZ-DASHBOARD (SUMMARY BAR) */}
            <div className="mt-4 bg-slate-900 border border-slate-700 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 shadow-lg">
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Anmeldungen</span>
                    <span className="text-xl font-black text-white font-mono">{financialTotals.registrations}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Teilnehmer</span>
                    <span className="text-xl font-black text-[#E6C299] font-mono">{financialTotals.participants}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Summe Netto (Einnahmen)</span>
                    <span className="text-xl font-black text-[#E6C299] font-mono">{formatCurrency(financialTotals.netto)}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Summe Brutto</span>
                    <span className="text-xl font-black text-emerald-400 font-mono">{formatCurrency(financialTotals.brutto)}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Kosten (Netto)</span>
                    <span className="text-xl font-black text-red-400 font-mono">{formatCurrency(financialTotals.totalCosts)}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Reingewinn (Netto)</span>
                    <span className="text-xl font-black text-emerald-400 font-mono">{formatCurrency(financialTotals.netProfit)}</span>
                </div>
                <div className="w-px h-10 bg-slate-800 hidden md:block"></div>
                <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Überschuss (Brutto)</span>
                    <span className="text-xl font-black text-emerald-400 font-mono">{formatCurrency(financialTotals.grossProfit)}</span>
                </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                 <p className="text-xs text-slate-500">
                   {(activeEvent?.participants || []).filter((p) => p.invoiceMailSentAt).length} Mail versendet
                   {' · '}
                   {(activeEvent?.participants || []).filter((p) => p.rechnungNr && !p.invoiceMailSentAt).length} Mail offen
                 </p>
                 <div className="flex flex-wrap justify-end gap-3">
                   <button
                      type="button"
                      onClick={() => { void handleBulkSendMails(); }}
                      disabled={activeEvent?.participants.length === 0 || isSaving || isGeneratingPDF || !!bulkMailProgress}
                      className="px-6 py-4 bg-emerald-900/40 hover:bg-emerald-600 text-emerald-300 hover:text-white border border-emerald-500/40 rounded-xl font-black uppercase tracking-widest text-sm flex items-center gap-2.5 transition-all disabled:opacity-50"
                   >
                      <Mail className="w-5 h-5" />
                      {bulkMailProgress
                        ? `Sende ${bulkMailProgress.done}/${bulkMailProgress.total}…`
                        : 'Offene Mails absenden'}
                   </button>
                   <button
                      onClick={handleGenerateInvoices}
                      disabled={activeEvent?.participants.length === 0 || isSaving || isGeneratingPDF}
                      className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black uppercase tracking-widest text-sm flex items-center gap-2.5 transition-all shadow-[0_0_20px_rgba(16,185,129,0.2)] disabled:opacity-50"
                   >
                      <Download className="w-5 h-5" /> {isSaving ? 'Verarbeite...' : 'Sammel-PDF herunterladen'}
                   </button>
                 </div>
            </div>
          </div>
        </div>

        {/* Repariertes UI-Preview Panel mit sauberem Scaling! */}
        <div className="preview-panel xl:sticky xl:top-4 xl:h-[calc(100vh-2rem)] flex flex-col relative">
            {isGeneratingPDF && (
                <div className="absolute inset-0 z-50 bg-[#0f172a]/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl border border-emerald-500/50 shadow-[0_0_50px_rgba(16,185,129,0.2)]">
                    {bulkMailProgress ? (
                      <Mail className="w-12 h-12 text-emerald-400 animate-bounce mb-4" />
                    ) : (
                      <Download className="w-12 h-12 text-emerald-400 animate-bounce mb-4" />
                    )}
                    <p className="text-white font-black tracking-widest uppercase">
                      {bulkMailProgress
                        ? `Sammel-Mail ${bulkMailProgress.done}/${bulkMailProgress.total}`
                        : 'Verarbeite Rechnung...'}
                    </p>
                    <p className="text-slate-400 text-xs mt-2">Das kann einen Moment dauern.</p>
                </div>
            )}
            <p className="text-center text-xs text-slate-500 font-mono tracking-wider uppercase bg-[#0f172a] p-3 rounded-lg border border-slate-800 mb-3">Live-Vorschau für Teilnehmer {previewIndex + 1}</p>
            {previewInvoiceData && activeEvent ? (
                <div className="border border-slate-800 rounded-2xl flex-grow shadow-2xl bg-slate-900 overflow-hidden flex justify-center pt-8">
                    {/* WICHTIG: Hier skalieren wir das Dokument optisch herunter, ohne sein Pixel-Layout kaputtzumachen */}
                    <div className="origin-top scale-[0.45] md:scale-[0.7] transition-transform" style={{ width: '794px' }}>
                        <InvoiceDocument pData={previewInvoiceData} sData={activeEvent} pNr={previewIndex} />
                    </div>
                </div>
            ) : (
                <div className="bg-[#0f172a] rounded-2xl border border-slate-800 flex-grow flex items-center justify-center italic text-slate-600">
                    Klicken Sie auf einen Teilnehmer für die Vorschau.
                </div>
            )}
        </div>
      </div>

      {/* Sicherer, vollständig im DOM befindlicher Offscreen-Container für html2canvas */}
      {/* 💉 CHIRURGIE-FIX: ID auf event-print-factory geändert */}
      <div id="event-print-factory" style={{ position: 'absolute', top: '-50000px', left: '-50000px' }}>
        <div style={{ width: '794px' }}>
            {activeEvent?.participants.map((p, index) => {
                const totals = getParticipantTotals(p, activeEvent);
                const nr = p.rechnungNr || `ENTWURF-${index}`;
                const formattedDate = formatInvoiceDate(getParticipantInvoiceDate(p, activeEvent));
                
                const pData = { participant: p, baseNetto: totals.baseNetto, zusatzNetto: totals.zusatzNetto, cateringNetto: totals.cateringNetto, discountNetto: totals.discountNetto, netto: totals.netto, vat: totals.vat, brutto: totals.brutto, nr, isReverseCharge: totals.isReverseCharge, formattedDate: formattedDate };

                return <InvoiceDocument key={p.id} pData={pData} sData={activeEvent} pNr={index} />
            })}
        </div>
      </div>
    </div>
  );
};

export default AdminInvoiceCreator;